/* eslint-disable react/display-name */
/* eslint-disable react/no-unescaped-entities */
// libraries
import React from 'react';
import styled from 'styled-components';

// assets
import Acupuncture from 'assets/icons/services/Acupuncture.svg';
import BirthEducation from 'assets/icons/services/BirthEducation.svg';
import Chiropractic from 'assets/icons/services/Chiropractic.svg';
import Doula from 'assets/icons/services/Doula.svg';
import Exercise from 'assets/icons/services/Exercise.svg';
import FertilityCoaching from 'assets/icons/services/FertilityCoaching.svg';
import Coaching from 'assets/icons/services/Coaching.svg';
import InfantSleep from 'assets/icons/services/InfantSleep.svg';
import Lactation from 'assets/icons/services/Lactation.svg';
import MassageTherapy from 'assets/icons/services/MassageTherapy.svg';
import MentalHealth from 'assets/icons/services/MentalHealth.svg';
import Nutrition from 'assets/icons/services/Nutrition.svg';
import PhysicalTherapy from 'assets/icons/services/PhysicalTherapy.svg';
import OccupationalTherapy from 'assets/icons/services/OccupationalTherapy.svg';

import AcupunctureImage from 'assets/images/servicesLanding/Acupuncture.jpg';
import BirthEducationImage from 'assets/images/servicesLanding/BirthEducation.jpg';
import ChiropracticImage from 'assets/images/servicesLanding/Chiropractic.jpg';
import DoulaImage from 'assets/images/servicesLanding/Doula.jpg';
import ExerciseImage from 'assets/images/servicesLanding/Exercise.jpg';
import FertilityCoachingImage from 'assets/images/servicesLanding/FertilityCoaching.jpg';
import CoachingImage from 'assets/images/servicesLanding/Coaching.jpg';
import InfantSleepImage from 'assets/images/servicesLanding/InfantSleep.jpg';
import LactationImage from 'assets/images/servicesLanding/Lactation.jpg';
import MassageTherapyImage from 'assets/images/servicesLanding/MassageTherapy.jpg';
import MentalHealthImage from 'assets/images/servicesLanding/MentalHealth.jpg';
import NutritionImage from 'assets/images/servicesLanding/Nutrition.jpg';
import PhysicalTherapyImage from 'assets/images/servicesLanding/PhysicalTherapy.jpg';
import OccupationalTherapyImage from 'assets/images/servicesLanding/OccupationalTherapy.jpg';

const StyledLink = styled.a<any>`
  color: ${({ theme: { colors } }): string => colors.bismark};
`;

const StyledH2 = styled.h2`
  font-size: 1em;
  display: inline-block;
`;

export const SERVICE_ICONS = {
  Acupuncture,
  BirthEducation,
  Chiropractic,
  Doula,
  Exercise,
  InfantSleep,
  Lactation,
  MassageTherapy,
  MentalHealth,
  Nutrition,
  Coaching,
  FertilityCoaching,
  PhysicalTherapy,
  OccupationalTherapy,
};

export const SERVICE_IMAGES = {
  Acupuncture: AcupunctureImage,
  BirthEducation: BirthEducationImage,
  Chiropractic: ChiropracticImage,
  Doula: DoulaImage,
  Exercise: ExerciseImage,
  InfantSleep: InfantSleepImage,
  Lactation: LactationImage,
  MassageTherapy: MassageTherapyImage,
  MentalHealth: MentalHealthImage,
  Nutrition: NutritionImage,
  Coaching: CoachingImage,
  FertilityCoaching: FertilityCoachingImage,
  PhysicalTherapy: PhysicalTherapyImage,
  OccupationalTherapy: OccupationalTherapyImage,
};

export const CONTENT_SERVICES = [
  {
    id: 'Acupuncture',
    title: 'Acupuncture for Fertility | Robyn',
    slug: '/service/acupuncture',
    description:
      'This ancient treatment may improve your odds of conceiving and help you feel better before, during and after pregnancy.',
    text:
      'This ancient treatment may improve your odds of conceiving and help you feel better before, during and after pregnancy.',
    h1: 'Acupuncture',
    subtitles: [
      {
        id: '1',
        title: 'Acupuncture for Fertility',
        asH2: true,
        text: (): JSX.Element => (
          <div>
            <div>
              Acupuncture is based on the theory that channels (meridians) run throughout your entire body, and that
              energy (called chi or qi) flows through them. But the proper flow of energy can get blocked, and when this
              happens it may lead to health problems—perhaps including infertility. Acupuncture aims to restore health
              by clearing those blockages. If you&#39;ve never had acupuncture treatment before, the idea of allowing
              someone to insert needles into your body might seem scary. But don&#39;t worry: The needles are sterilized
              and incredibly fine (like strands of hair), so the procedure is largely painless.
            </div>
            <br />
            <div>
              Although the evidence proving that this Chinese medicine therapy will help you get and stay pregnant is
              still emerging, there&#39;s good reason to believe that acupuncture for fertility may be worthwhile. In
              one recent study, conducted by{' '}
              <StyledLink
                href="https://www.sciencedirect.com/science/article/abs/pii/S1876382011000448"
                target="_blank"
                rel="noopener noreferrer"
              >
                researchers at Tel Aviv University in Israel
              </StyledLink>
              , women who used acupuncture and Traditional Chinese Medicine (TCM) herbs while undergoing IUI
              (intrauterine insemination) were significantly more likely to conceive and give birth compared to those
              who tried IUI without Chinese medicine. Other studies have found that acupuncture treatments may up the
              odds of{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6918533/"
                target="_blank"
                rel="noopener noreferrer"
              >
                IVF (in-vitro fertilization) embryo transfer success rates
              </StyledLink>
              , as well as the stress and side effects that come after receiving an IVF treatment or other reproductive
              medicines.
            </div>
            <br />
            <div>
              Some practitioners believe that acupuncture increases blood flow to the reproductive organs (including the
              ovaries and uterus) and helps regulate hormone level imbalances. Research has also suggested that
              acupuncture may help with infertility treatment by helping to{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3962314/"
                target="_blank"
                rel="noopener noreferrer"
              >
                regulate your menstrual cycle, ovulation and ease the pain of endometriosis
              </StyledLink>
              , all of which can interfere with pregnancy success rates. Acupuncture treatments may also help with
              blocked fallopian tubes, PCOS (polycystic ovarian syndrome), egg quality and even sperm quality in men.
              It&#39;s also possible that acupuncture may simply help relieve stress so you feel calmer. While stress
              alone is rarely responsible for fertility problems, people who struggle to conceive often report high
              levels of stress and anxiety, which makes some think that lower stress levels may help increase fertility.
              Acupuncture may make the entire process of getting pregnant feel less overwhelming.
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Acupuncture for Pregnancy',
        asH2: true,
        text: (): JSX.Element => (
          <div>
            <div>
              While seeking out acupuncture for fertility may help you get pregnant, you might want to continue with
              acupuncture sessions throughout your pregnancy. Some acupuncturists believe that acupuncture treatments
              can help sustain and nourish a healthy pregnancy, perhaps by optimizing the circulation of blood flow,
              oxygen and nutrients throughout your body and improving the conditions in your uterus. Acupuncture may
              also help you manage stress during your pregnancy, as well as alleviate symptoms of morning sickness,
              heartburn, and{' '}
              <StyledLink
                href="https://obgyn.onlinelibrary.wiley.com/doi/full/10.1111/j.0001-6349.2004.0215.x"
                target="_blank"
                rel="noopener noreferrer"
              >
                pain in your lower back and pelvis
              </StyledLink>
              . It might also play a role in soothing tender breasts, alleviating swollen feet, and reducing fatigue.
            </div>
            <br />
            <div>
              “In my practice, we focus on discomforts such as nausea, sleeplessness, anxiety, constipation, and low
              back pain during the first trimester,” says{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/tamara-zummallen/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Tamara ZumMallen, L.Ac.
              </StyledLink>
              , a California-based acupuncturist who's part of the Robyn network. “During the second trimester, my
              patients typically take a break from their acupuncture sessions. In the third trimester, we focus on
              healing aches and pains and keeping the baby’s head down.”
            </div>
            <br />
            <div>
              If you are past your due date, acupuncture might be used to{' '}
              <StyledLink
                href="https://www.merrionfetalhealth.ie/acupuncture-during-pregnancy-benefits-dos-donts/"
                target="_blank"
                rel="noopener noreferrer"
              >
                help induce labor
              </StyledLink>
              —with consent of your ob/gyn, of course. “When used correctly after 38 weeks, it can also be useful for
              helping dilation of the cervix to occur naturally, possibly reducing the need for Western medical
              intervention during birth,” says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/hillary-talbott/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Hillary Talbot, L.Ac.
              </StyledLink>
              , an acupuncturist in Florida.
            </div>
            <br />
            <div>
              Many acupuncturists are also happy to teach couples acupressure techniques that can be used during birth
              to mitigate the discomfort of contractions. (Acupressure works on the same points but involves
              pressure/massage rather than the insertion of needles.)
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Acupuncture for Postpartum Care',
        asH2: true,
        text: (): JSX.Element => (
          <div>
            <div>
              While giving birth to a healthy baby might seem like the ultimate goal, once you become a mom your journey
              is really just beginning. That can be exciting—but also extremely stressful. An estimated{' '}
              <StyledLink
                href="https://pediatrics.aappublications.org/content/131/4/e1218"
                target="_blank"
                rel="noopener noreferrer"
              >
                17 percent of women experience postpartum anxiety, and 5.5% experience depression
              </StyledLink>
              . Acupuncture works to support you during this time by promoting relaxation, reducing stress, and helping
              to rebalance your hormones. New motherhood can also be tough on your body—your back and joints in
              particular. A{' '}
              <StyledLink
                href="https://www.nccih.nih.gov/health/acupuncture-in-depth"
                target="_blank"
                rel="noopener noreferrer"
              >
                growing body of research
              </StyledLink>{' '}
              shows that acupuncture has very real, measurable benefits, especially when it comes to managing back,
              neck, and knee pain. Acupuncture has also been shown to help{' '}
              <StyledLink
                href="https://www.healthcmi.com/Acupuncture-Continuing-Education-News/1773-acupuncture-boosts-breast-milk-production"
                target="_blank"
                rel="noopener noreferrer"
              >
                aid breastfeeding
              </StyledLink>
              . One recent study of 116 women who were having trouble making enough breast milk found that those who
              tried acupuncture and traditional Chinese medicine nearly doubled their production.
            </div>
            <br />
            <div>
              “The postpartum period is one of the most important times to seek care,” says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/hillary-talbott/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Hillary Talbot, L.Ac.
              </StyledLink>
              , an acupuncturist in Florida. “Because of acupuncture’s well-documented effects on the endocrine and
              nervous systems, it can help a new mom feel less stressed during what can be a very demanding time.”
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'Lactation',
    title: 'Lactation | Robyn',
    slug: '/service/lactation',
    description:
      'Breastfeeding, which is one way to feed your child, can be wonderful—yet some parents find it extremely challenging. Expert help can go a long way.',
    text:
      'Breastfeeding, which is one way to feed your child, can be wonderful—yet some parents find it extremely challenging. A little expert help can go a long way.',
    h1: 'Lactation',

    subtitles: [
      {
        id: '1',
        title: 'Lactation',
        text: (): JSX.Element => (
          <div>
            <div>
              Breastfeeding might seem like the most natural thing in the world, but that doesn't mean it's always easy.
              The{' '}
              <StyledLink
                href="https://www.who.int/health-topics/breastfeeding#tab=tab_2"
                target="_blank"
                rel="noopener noreferrer"
              >
                World Health Organization
              </StyledLink>{' '}
              recommends that babies initiate breastfeeding within the first hour of birth and while most babies will
              instinctively try to nurse—sometimes within moments after being born—many have trouble latching on or
              nursing effectively. Meanwhile, a parent may experience pain and have concerns about whether or not
              they're producing enough milk to nourish their child. A lactation consultant or breastfeeding educator can
              help with all of these issues and more.
            </div>
            <br />
            <div>
              The{' '}
              <StyledLink
                href="https://pediatrics.aappublications.org/content/129/3/e827.full#content-block"
                target="_blank"
                rel="noopener noreferrer"
              >
                American Academy of Pediatrics
              </StyledLink>{' '}
              (AAP) recommends that babies be breastfed exclusively for the first six months of life and that you as a
              parent continue breastfeeding while gradually introducing solid foods for a baby’s first year. Of course,
              breastfeeding isn’t the right choice for everyone, and ultimately{' '}
              <StyledLink href="ps://wearerobyn.co/learn/fed-is-best/" target="_blank" rel="noopener noreferrer">
                “fed is best.”
              </StyledLink>{' '}
              But if you do decide to breastfeed, you should know that you don’t have to go it alone.
            </div>
            <br />
            <div>
              Breastfeeding your baby is associated with a wide variety of health benefits. Breast milk contains unique
              protective substances, including hormones and antibodies.{' '}
              <StyledLink
                href="https://www.womenshealth.gov/breastfeeding/making-decision-breastfeed#:~:text=Breastfeeding%20protects%20your%20baby%20from,temperature%20from%20dropping%20too%20low"
                target="_blank"
                rel="noopener noreferrer"
              >
                Research has found
              </StyledLink>{' '}
              that children who are breastfed as babies are less likely to develop asthma, ear infections, childhood
              obesity, eczema, gastrointestinal infections, frequent respiratory infections, and type 2 diabetes. Babies
              who are fed breast milk are also less likely to die from sudden infant death syndrome (SIDS).
            </div>
            <br />
            <div>
              Breastfeeding has benefits for you as a parent, too. Many nursing mothers enjoy the bonding experience it
              provides, and being physically close to your baby is just part of it: When you breastfeed your body
              produces{' '}
              <StyledLink
                href="https://www.healthychildren.org/English/ages-stages/baby/breastfeeding/Pages/Benefits-of-Breastfeeding-for-Mom.aspx"
                target="_blank"
                rel="noopener noreferrer"
              >
                prolactin
              </StyledLink>
              , which promotes relaxation, as well as oxytocin, the "love" hormone, which facilitates attachment.
              Oxytocin also serves to help shrink the uterus after childbirth and slow post-childbirth bleeding.
            </div>
            <br />
            <div>
              Meanwhile, breastfeeding may improve women’s health by reducing the risk of later developing breast cancer
              and ovarian cancer. Research has also suggested that it might lower your chances of getting type 2
              diabetes, cardiovascular disease, and rheumatoid arthritis, too.
            </div>
            <br />
            <div>
              Breastfeeding can also serve as a form of{' '}
              <StyledLink
                href="https://www.llli.org/breastfeeding-info/fertility/#:~:text=Can%20I%20get%20pregnant%20while,Method%20(LAM)%20of%20contraception."
                target="_blank"
                rel="noopener noreferrer"
              >
                natural contraception
              </StyledLink>
              . This birth control "method" isn't fool-proof, but it generally works if you're exclusively breastfeeding
              around the clock and your period hasn't returned.
            </div>
            <br />
            <div>
              Despite all these benefits, many women who say they intend on exclusive breastfeeding end up abandoning
              their plans rather quickly.{' '}
              <StyledLink
                href="https://blog.chocchildrens.org/do-i-need-a-lactation-consultant/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sometimes breastfeeding
              </StyledLink>{' '}
              mothers experience pain, during their let-down or engorgement, and may develop sore nipples or even
              infections like mastitis. In other instances, the baby has trouble latching, acts fussy while feeding, or
              refuses to nurse. Many parents also worry that an exclusively breastfed baby isn't getting enough milk,
              especially during the first few days after birth.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'Nutrition',
    title: 'Postpartum Nutrition | Robyn',
    slug: '/service/nutrition',
    description:
      'Making smart food choices may help you get pregnant, grow a healthy baby, and recover after you give birth.',
    text: 'Making smart food choices may help you get pregnant, grow a healthy baby, and recover after you give birth.',
    h1: 'Nutrition',

    subtitles: [
      {
        id: '1',
        title: 'Nutrition for Fertility',
        text: (): JSX.Element => (
          <div>
            <div>
              Wouldn&#39;t it be great if there was one magic food you could eat that would guarantee you&#39;d get
              pregnant, feel great through your pregnancy, and give birth to a healthy baby? Unfortunately, it's not
              quite that simple when it comes to reproductive health, but there are still plenty of good reasons to eat
              as healthfully as possible when you&#39;re trying to conceive. There&#39;s even some evidence that doing
              so might up your odds of getting pregnant.
            </div>
            <br />
            <div>
              If you&#39;re seeking a detailed plan, you should know that some women have benefitted from "The Fertility
              Diet," which is a plan based on a nutrition research that was conducted at the Harvard School of Public
              Health. The best part about this plan, says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/sarah-rueven/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sarah Rueven
              </StyledLink>
              , MS, RDN, CDN, is that it&#39;s healthy for everyone—even men and women who aren&#39;t trying to
              conceive. The{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/what-is-the-fertility-diet/"
                target="_blank"
                rel="noopener noreferrer"
              >
                key components
              </StyledLink>
              : fiber-rich whole grains, healthy fats (like those found in olive oil, avocado, and salmon), lots of
              plant-based protein, and a moderate amount of full-fat dairy.
            </div>
            <br />
            <div>
              If you&#39;re not looking to overhaul your entire way of eating, simply incorporating a few new healthy
              foods into your diet can still be worthwhile.{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/10-foods-to-eat-now-as-you-start-to-think-about-ttc/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Beets
              </StyledLink>
              , for instance, contain nitrate, a compound that may improve blood flow to the uterus. Also consider
              swapping white potatoes for sweet ones: Sweet potatoes are higher in fiber (eat the skin for extra), and
              they&#39;re also a good source of vitamin B6, which plays a role in the multiplication of cells. Vitamin
              E–rich almonds are another good addition to your diet because they may improve your egg quality.
            </div>
            <br />
            <div>
              Meanwhile, be sure to limit your consumption of fast food. Too many processed foods may contribute to
              obesity and inflammation, both of which may interfere with your efforts to get pregnant. A 2018 study from
              the journal{' '}
              <StyledLink href="https://pubmed.ncbi.nlm.nih.gov/29733398/" target="_blank" rel="noopener noreferrer">
                Human Reproduction
              </StyledLink>{' '}
              found that women who eat a lot of fast food tend to have a harder time getting pregnant than those who
              rarely indulge in it.
            </div>
            <br />
            <div>
              Lastly, don&#39;t forget to get extra folic acid (in supplement form). Talk to your provider, but a
              general recommendation is to take a prenatal vitamin with{' '}
              <StyledLink
                href="https://www.cdc.gov/ncbddd/folicacid/about.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                400 mcg of folic acid daily
              </StyledLink>{' '}
              well before you get pregnant to reduce the risk of serious birth defects, such as neural tube defects.
              These birth defects occur very early in pregnancy—before most people even know they're pregnant—so don't
              delay.
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Nutrition During Pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Pregnant women should know that being pregnant doesn&#39;t exactly mean eating for two. Yes, you need some
              extra calories, but don&#39;t go overboard and beware of excessive weight gain: You only need about{' '}
              <StyledLink
                href="https://medlineplus.gov/ency/patientinstructions/000584.htm#:~:text=Pregnant%20women%20need%20about%20300,the%20nutrients%20your%20baby%20needs."
                target="_blank"
                rel="noopener noreferrer"
              >
                300 more calories
              </StyledLink>{' '}
              per day to maintain a healthy pregnancy, and quality matters! When it comes to the extras, focus most on
              foods that are rich in protein, fiber, and calcium.{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/pregnancy-and-iron-deficiency-what-you-need-to-know/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Iron deficiency
              </StyledLink>{' '}
              is also common during pregnancy, due to the increase in blood volume that makes iron absorption more
              difficult, so be sure to include iron-rich foods in your diet. You might even consider taking iron
              supplements, but be sure to discuss any supplements with your healthcare providers first.
            </div>
            <br />
            <div>
              Pregnant people who suffer from morning sickness often struggle with the opposite problem of how to get
              enough nutrients despite feeling queasy. Robyn dietitian{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/tracy-b/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Tracy Lockwood Beckerman
              </StyledLink>{' '}
              MS, RD, recommends pre-empting nausea by eating small amounts of plain, dry foods like pretzels and
              crackers throughout the day.{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/top-5-food-remedies-for-morning-sickness-from-a-registered-dietitian/"
                target="_blank"
                rel="noopener noreferrer"
              >
                She also notes
              </StyledLink>{' '}
              that sipping bone broth may help keep you hydrated while providing essential nutrients like calcium and
              magnesium. Meanwhile, seeds, beans, and legumes may help alleviate nausea while providing vitamin B6. B
              vitamins help your baby&#39;s brain and nervous system develop properly. It’s also a good idea to
              incorporate foods high in vitamin C into your diet which has benefits for both you and baby, and helps
              boost your immune system. Finally, be sure to discuss your vitamin A intake with your health care
              provider.
            </div>
            <br />
            <div>
              <StyledLink
                href="https://www.mayoclinic.org/healthy-lifestyle/pregnancy-week-by-week/in-depth/pregnancy-nutrition/art-20043844"
                target="_blank"
                rel="noopener noreferrer"
              >
                Foods to avoid
              </StyledLink>{' '}
              for pregnant women include anything raw or undercooked, seafood that&#39;s high in mercury (like Bigeye
              tuna and swordfish), unpasteurized cheeses, and alcohol. Some caffeine is OK, but you should limit it:
              Plan to cut yourself off after{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/ask-the-rd-can-i-still-get-my-caffeine-on-during-pregnancy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                1-2 cups of coffee
              </StyledLink>{' '}
              each day.
            </div>
            <br />
            <div>
              Overall, your diet during pregnancy should look a lot like your pre-pregnancy diet—assuming you were
              already focusing on your health care and getting plenty of produce, whole grains, and some lean protein
              and don’t have any pregnancy complications. If you do develop gestational diabetes, for example, health
              care professionals may recommend making more extensive changes to your diet, such as cutting back on
              carbohydrates, to keep your blood sugar as stable as possible. People with gestational diabetes who aren’t
              able to control the condition are more apt to have additional complications such as preeclampsia, which
              can cause negative birth outcomes such as low birth weight or preterm birth.
            </div>
            <br />
            <div>If you need help with the basics, seek out a Robyn nutritionist for personalized guidance.</div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Postpartum Nutrition',
        asH2: true,
        text: (): JSX.Element => (
          <div>
            <div>
              Taking time to nourish yourself while caring for a new baby isn't always easy, but it's important. As with
              other stages of your life, a healthy diet featuring a variety of whole grains, plenty of fruits and
              vegetables, and some lean protein is generally best.
            </div>
            <br />
            <div>
              Many new moms want to quickly reverse the weight gain that occurred during pregnancy, but rushing weight
              loss can backfire so consider any and all risk factors. Instead of turning to drastic diet plans that
              aren't sustainable, focus on{' '}
              <StyledLink
                href="https://www.stanfordchildrens.org/en/topic/default?id=the-new-mother---taking-care-of-yourself-after-birth-90-P02693"
                target="_blank"
                rel="noopener noreferrer"
              >
                cutting out empty-calorie snacks
              </StyledLink>{' '}
              and loading up on nutritious fruits and vegetables. Getting more exercise can also help.
            </div>
            <br />
            <div>
              If you're breastfeeding, keep in mind that some of the byproducts of foods you eat can end up in your
              breast milk. For that reason, you should continue limiting your intake of fish that's high in mercury,
              spicy foods, and gassy foods like beans, broccoli, and cabbage. You should be careful not to consume too
              much caffeine or alcohol, warns Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/amy-s/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Amy Shapiro
              </StyledLink>
              , MS, RD, CDN.{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/breastfeeding-diet-food-limitations-even-after-giving-birth/"
                target="_blank"
                rel="noopener noreferrer"
              >
                She also advises
              </StyledLink>{' '}
              staying well-hydrated by drinking plenty of water throughout the day.
            </div>
            <br />
            <div>
              Moms who are having trouble producing enough milk might benefit from eating more{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/top-foods-that-help-with-lactation/"
                target="_blank"
                rel="noopener noreferrer"
              >
                oatmeal
              </StyledLink>
              , which is believed to help increase milk supply. Wild salmon is also a good pick for breastfeeding moms.
              "It is plentiful in DHA and EPA, omega-3 fatty acids that are expressed in breast milk and support your
              baby’s healthy brain and nervous system development,"{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/top-foods-that-help-with-lactation/"
                target="_blank"
                rel="noopener noreferrer"
              >
                says Rueven
              </StyledLink>
              . "Salmon also is rich in vitamin B12 and vitamin D."
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'MentalHealth',
    title: 'Mental Health and Pregnancy | Robyn',
    slug: '/service/mental-health',
    description:
      'Check in on your mental health to thrive on your path to parenthood. A healthy baby starts with a healthy parent.',
    text: 'Check in on your mental health to thrive on your path to parenthood.',
    h1: 'Mental Health',

    subtitles: [
      {
        id: '1',
        title: 'Mental Health Before Pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              A healthy baby starts with a healthy parent. While many know that it's wise to get their physical health
              in order before trying to conceive—maybe you've already started eating better or exercising more—your
              mental health is just as important
            </div>
            <br />
            <div>
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3605892/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Research
              </StyledLink>{' '}
              has shown that those who have poor mental health before they get pregnant are at higher risk to have
              complications during pregnancy, including miscarriage. Unchecked{' '}
              <StyledLink
                href="https://link.springer.com/article/10.1007/s00737-019-0941-7"
                target="_blank"
                rel="noopener noreferrer"
              >
                depression
              </StyledLink>{' '}
              can be especially problematic, but other mental health issues, such as anxiety, should also be addressed
              as soon as possible.
            </div>
            <br />
            <div>
              Checking in on your mental health no matter what is normal and important. If you have a pre-existing
              condition like depression or bipolar disorder, it’s absolutely essential that you set up the plan that
              works best and make sure you are checking in and setting follow-up appointments with your health care
              provider regularly. If you've been taking antidepressants or other medication for a mental health problem
              such as severe depression, talk to your doctor about whether it's safe to continue doing so throughout
              pregnancy. There are{' '}
              <StyledLink
                href="https://womensmentalhealth.org/specialty-clinics/psychiatric-disorders-during-pregnancy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                pros and cons
              </StyledLink>{' '}
              to continuing any medication during pregnancy, so be sure to discuss them thoroughly with your health care
              provider: All drugs have side effects and risk factors, but stopping treatment you need can also cause
              increased risk and become dangerous for you and your baby. Your provider might also suggest changing
              treatment options such as updating your dose or switching you to a safer medication.
            </div>
            <br />
            <div>
              Now is also a good time to make sure you have a strong support system in place. In addition to a trusted
              ob/gyn, you may want to identify a therapist you feel comfortable talking to. Meanwhile, spend some time
              thinking about which friends and family members will provide social and emotional support and be most
              helpful when you need help managing your stress during your pregnancy and beyond.
            </div>
            <br />
            <div>
              If you're struggling with infertility, you should know that it's not at all unusual for people in this
              situation to become stressed, anxious, or depressed—even if they’ve never had any mental health problems
              or struggled with mental illness before. Sometimes taking extra time for self-care (by exercising more,
              practicing{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/5-ways-to-manage-stress-throughout-your-fertility-journey/"
                target="_blank"
                rel="noopener noreferrer"
              >
                mindfulness
              </StyledLink>
              , or getting regular massages, for instance), can make all the difference. But many people also benefit
              from professional help in the form of "infertility-focused therapy," says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/lindsay-l/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Lindsay Liben
              </StyledLink>
              , LCSW, a New York-based psychotherapist.
            </div>
            <br />
            <div>
              "While each treatment is unique, there are a few common themes in the work. First, we process the wide
              range of emotions you are experiencing, which helps you to understand, grieve, and cope with your
              feelings,"{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/infertility-therapy-explained/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Liben explains
              </StyledLink>
              . "Additionally, we examine the underlying expectations and pressures that influence your emotions."
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Mental Health During Pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Pregnancy can be an exciting time in a woman’s life, but also a stressful one. Women who have underlying
              mental health issues, like{' '}
              <StyledLink
                href="https://kidshealth.org/en/parents/pregnant-mental-health.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                clinical depression, generalized anxiety, or an eating disorder
              </StyledLink>
              , should be sure to stay in close contact with their provider during their antenatal care. But even
              pregnant women with no history of mental health issues may find themselves struggling as they adjust to
              their changing bodies, try to make sense of the "rules" about what they can and can't do while pregnant,
              and plan to welcome a brand-new person into their lives. Hormonal changes can also take a major toll on
              your mood.
            </div>
            <br />
            <div>
              Self-care practices and a good social support system can help, at least to a certain extent. If you feel
              extremely overwhelmed, anxious, or depressed, reach out to a{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialists/mental-health/"
                target="_blank"
                rel="noopener noreferrer"
              >
                mental health expert
              </StyledLink>
              . About{' '}
              <StyledLink
                href="https://www.mayoclinic.org/healthy-lifestyle/pregnancy-week-by-week/in-depth/depression-during-pregnancy/art-20237875"
                target="_blank"
                rel="noopener noreferrer"
              >
                7% of women
              </StyledLink>{' '}
              develop clinical depression during pregnancy, but you don't need to be officially diagnosed with perinatal
              depression or anything else to get some extra help managing your emotions and behaviors during this stage
              of your life.
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Postpartum Mental Health',
        text: (): JSX.Element => (
          <div>
            <div>
              After you give birth, women’s hormones can fluctuate wildly. There is data that the matter that makes up
              our brains even physically changes during the postpartum period after we’ve given birth. Add caring for a
              newborn and lack of sleep to the mix and it's no surprise that many women experience sadness and stress,
              at least for a little while. But there is a spectrum of changes you may endure with regards to postnatal
              mental health from baby blues, postpartum or postnatal depression, mental disorders, anxiety disorders,
              postpartum psychosis, and PTSD. It is not just postpartum depression or nothing, and that is important to
              understand and normalize. There's a big difference between experiencing the fleeting{' '}
              <StyledLink
                href="https://www.womenshealth.gov/mental-health/mental-health-conditions/postpartum-depression"
                target="_blank"
                rel="noopener noreferrer"
              >
                "baby blues"
              </StyledLink>{' '}
              and postpartum depression.
            </div>
            <br />
            <div>
              Postpartum or postnatal depression can crop up at any point during the first year of your baby's life.
              While the baby blues generally dissipate within a few days, postpartum depression lasts more than two
              weeks and tends to get increasingly worse. A strong support system—perhaps including your partner,
              extended family, and/or a postpartum doula—can help take some of the pressure off, or at least allow you
              to get a much-needed rest when you're feeling spent.
            </div>
            <br />
            <div>
              True postpartum depression usually requires professional help. If you're suffering from{' '}
              <StyledLink
                href="https://www.acog.org/patient-resources/faqs/labor-delivery-and-postpartum-care/postpartum-depression"
                target="_blank"
                rel="noopener noreferrer"
              >
                postpartum depression
              </StyledLink>{' '}
              —or just feeling like you could use someone to talk to during this turbulent time—don't hesitate to reach
              out to a mental health expert, even if you've never seen one before. If you develop serious symptoms of
              depression, however, it's especially important not to delay. Those may include relentless sadness and
              hopelessness as well as loss of pleasure in things you used to enjoy. You may also have trouble caring for
              yourself or your baby, or have thoughts of hurting yourself or your baby. Getting the right help (which
              might include talk therapy and/or medication) can help you and your new baby thrive in this time after one
              of your biggest life events.
            </div>
            <br />
            <div>
              While postpartum depression has gotten a lot more attention in recent years, anxiety is a common problem,
              too. About{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/what-is-postpartum-anxiety/"
                target="_blank"
                rel="noopener noreferrer"
              >
                10% of women
              </StyledLink>{' '}
              suffer from postpartum anxiety disorders. While it's natural to feel vigilant about protecting your baby,
              women with postpartum anxiety often report feeling extremely scared, panicky, or angry. As with postpartum
              depression, postpartum anxiety can develop any time during your baby's first year. An expert can help you
              work through your stressful thoughts and feelings.
            </div>
            <br />
            <div>
              "A good number of postpartum women who have come to my practice have{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/what-is-postpartum-anxiety/"
                target="_blank"
                rel="noopener noreferrer"
              >
                not shared their experiences with anyone
              </StyledLink>{' '}
              in their lives due to fear and shame. Yet when they find the strength to speak about what they are going
              through, most find that other postpartum women in their lives have shared a similar experience with
              postpartum anxiety," says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/batya-novick/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Batya Novick
              </StyledLink>
              , LCSW, a therapist who specializes in reproductive mental health.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'Doula',
    title: 'Doula Services | Robyn',
    slug: '/service/doula',
    description:
      'A doula is a trained professional who understands the physical and emotional experience of birth and can guide you through the process and beyond.',
    text:
      'A doula is a trained professional who understands the physical and emotional experience of birth and can guide you through the process and beyond.',
    h1: 'Doula Services',
    subtitles: [
      {
        id: '1',
        title: 'Birth Doula',
        text: (): JSX.Element => (
          <div>
            <div>
              Whether you're about to become a parent for the first time or already have a few kids at home, you
              probably know that each birth is different and things don't always go according to plan. How can you
              ensure that your physical and emotional needs will be prioritized and that you'll get all the information
              you want so you can make smart choices? Hiring a doula might be the answer.
            </div>
            <br />
            <div>
              A doula can't guarantee that your version of the perfect birth will come true. She can't actually deliver
              your baby, either. (You'll need an ob/gyn or midwife for that.) Yet a birth doula can play an invaluable
              role before and during childbirth by educating you about your options, facilitating communication with
              medical professionals, and keeping you as calm and comfortable as possible throughout labor and delivery.
            </div>
            <br />
            <div>
              A doula’s main job is to support you and your partner if you have one. The goal of doula support is not to
              tell you what choices to make but rather present you with your options and encourage you to follow the
              path that’s right for you.
            </div>
            <br />
            <div>
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3647727/#:~:text=Expectant%20mothers%20matched%20with%20a,more%20likely%20to%20initiate%20breastfeeding."
                target="_blank"
                rel="noopener noreferrer"
              >
                Research
              </StyledLink>{' '}
              has shown that involving a doula lowers the chances of having a low birth weight baby or experiencing a
              birth complication. Women who use doula services are also{' '}
              <StyledLink
                href="https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD003766.pub6/full"
                target="_blank"
                rel="noopener noreferrer"
              >
                less likely to require a c-section
              </StyledLink>{' '}
              and tend to be in labor for less time before giving birth.
            </div>
            <br />
            <div>
              So what does a birth doula actually do? "We can help you find childbirth classes, help you formulate
              questions for your care provider, provide{' '}
              <StyledLink href="https://wearerobyn.co/learn/what-is-a-doula/" target="_blank" rel="noopener noreferrer">
                labor support
              </StyledLink>{' '}
              with hip squeezes while also reminding you to hydrate and telling you how amazing you are because you're
              about to give birth to another human!" says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/erin-p/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Erin Pasquet
              </StyledLink>{' '}
              , a DONA-trained birth doula.
            </div>
            <br />
            <div>
              A doula might also massage your back to help you relax, walk with you when contractions increase, feed you
              ice chips, and hold your hand when your partner needs to run out to use the bathroom or grab a bite to
              eat.
            </div>
            <br />
            <div>
              One common myth is that doulas are only for parents intent on having a "natural" birth—meaning a vaginal
              birth with no anesthesia. While it's true that women who use a doula are{' '}
              <StyledLink
                href="https://evidencebasedbirth.com/the-evidence-for-doulas/"
                target="_blank"
                rel="noopener noreferrer"
              >
                less likely to opt for an epidural,
              </StyledLink>{' '}
              forgoing pain relief is hardly a requirement. Whether you get an epidural or not, a doula can help
              position you more comfortably and{' '}
              <StyledLink
                href="https://www.yourdoulabag.com/blogs/yourdoulabag/17271168-epidural-6-ways-a-doula-can-support"
                target="_blank"
                rel="noopener noreferrer"
              >
                provide on-the-spot coaching
              </StyledLink>{' '}
              when it's time to push.
            </div>
            <br />
            <div>
              A birth doula also serves as a familiar face throughout the entire process: You'll likely meet them
              several times during your pregnancy to discuss your wishes and plans. When you go into labor, they will be
              one of the first people you call; they will meet you at your home or at your hospital/birthing center and
              typically stay with you until after your baby is born.
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Postpartum Doula',
        text: (): JSX.Element => (
          <div>
            <div>
              Each time a child is born, a parent is born too. Postpartum care and support is not only normal, it is
              essential. A postpartum doula&#39;s job is to support you and your new family after your baby is born
              during the postpartum period. Postpartum doula services offer one of the best ways to get the support you
              need as a new parent. (Some doulas serve as both birth doulas and postpartum doulas, but many fill only
              one of these roles.)
            </div>
            <br />
            <div>
              In some ways, a postpartum doula may seem similar to a baby nurse, but there are some important
              differences. While a baby nurse primarily focuses on the newborn&#39;s needs, a postpartum doula is
              trained to tend to the entire family. That means she can{' '}
              <StyledLink
                href="https://www.dona.org/what-is-a-doula/benefits-of-a-doula/"
                target="_blank"
                rel="noopener noreferrer"
              >
                help teach you about how to care for your new baby
              </StyledLink>
              , cook meals, organize the systems of your home like bottle stations and changing stations, do laundry,
              and provide both emotional support and physical support for you, your partner, and other children you may
              have. Postpartum doulas can also help with breastfeeding techniques and feeding strategies. If you need
              specific breastfeeding support though, you should contact a Certified Lactation Consultant (CLC).
            </div>
            <br />
            <div>
              As with a birth doula, a postpartum doula isn&#39;t there to insist that you do things a certain way but
              rather to provide postpartum support by educating and informing you so you can make the best possible
              decisions about feeding, sleep, and more.
            </div>
            <br />
            <div>
              A postpartum doula may also provide guidance as you{' '}
              <StyledLink
                href="https://icea.org/wp-content/uploads/2015/12/Role-Scope-of-Postpartum-Doula-3.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                recover from childbirth
              </StyledLink>{' '}
              and help you better understand what&#39;s "normal" for you post-pregnancy. This is an especially important
              role seeing as most women do not see their healthcare providers until 6 weeks after they give birth, yet
              during that time many new moms deal with issues such as pain, incontinence, insomnia, exhaustion, mood
              swings, and postpartum depression or anxiety. While a postpartum doula can&#39;t offer medical treatment,
              she can serve to{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3048903/"
                target="_blank"
                rel="noopener noreferrer"
              >
                bridge the gap
              </StyledLink>{' '}
              by answering questions and addressing concerns that arise for a new mother during this period.
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Finding a Doula',
        text: (): JSX.Element => (
          <div>
            <div>
              Whether you&#39;re looking for a birth doula, a postpartum doula, or both, finding the right fit is
              extremely important. "Personality match is the top thing to look for in a doula, "{' '}
              <StyledLink href="https://wearerobyn.co/learn/what-is-a-doula/" target="_blank" rel="noopener noreferrer">
                says Pasquet
              </StyledLink>
              . A doula provides an intimate service, so you need to feel at ease about having them by your side and
              feel confident that they will support you and advocate for you. If possible, candidates should meet with
              you and your partner, if you have one, since it&#39;s important that you both feel comfortable.
            </div>
            <br />
            <div>
              When you're interviewing potential candidates, be sure to ask about their doula training, certification({' '}
              <StyledLink
                href="https://www.dona.org/become-a-doula/birth-doula-certification/"
                target="_blank"
                rel="noopener noreferrer"
              >
                DONA International
              </StyledLink>{' '}
              is a well-respected organization that issues certification), and experience in terms of how long they've
              been practicing and how many births they&#39;ve attended. If you know that you are having twins, or have a
              pre-existing condition, it might be nice to work with a doula who specializes in your needs. It's also
              important to find out if the doula has a backup doula who can step in if two clients end up going into
              labor at the same time.
            </div>
            <br />
            <div>
              Pricing usually varies by experience as well as the length of the arrangement. Many birth doulas offer
              packages that include a certain number of sessions leading up to the birth as well the labor/birth itself.
              Postpartum doulas might charge by the day, week, or specific hours.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'PhysicalTherapy',
    title: 'Physical Therapy During Pregnancy | Robyn',
    slug: '/service/physical-therapy',
    description:
      'During pregnancy your posture, joint alignment and muscle strength, and flexibility may shift in unexpected ways that may affect your quality of life.',
    text:
      "Physical therapy can help ensure that you're strong and healthy as you transition from fertility into parenthood.",
    h1: 'Physical Therapy',

    subtitles: [
      {
        id: '1',
        title: 'Physical therapy before pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Many people associate a Doctor of Physical Therapy (DPT) with recovering from an injury or disease that
              has made it difficult to move around properly. While many physical therapists certainly focus on those
              sorts of situations—they play an integral role in sports medicine and athletic rehab while also helping
              stroke survivors improve their gait, for instance—some specialize in women's health. These experts are
              especially well-versed in helping women prepare for pregnancy, thrive during it, and heal afterward.
            </div>
            <br />
            <div>
              According to the{' '}
              <StyledLink
                href="https://www.choosept.com/resources/detail/5-ways-to-prepare-your-body-pregnancy"
                target="_blank"
                rel="noopener noreferrer"
              >
                American Physical Therapy Association
              </StyledLink>
              , there are many good reasons to start physical therapy before you conceive. Those include toning your
              pelvic floor muscles, improving your posture, and strengthening your core.
            </div>
            <br />
            <div>
              Your core muscles, for obvious reasons, will undergo a lot of changes once you get pregnant. It’s prudent
              to strengthen those muscles before you get pregnant. A strong core is crucial for good balance, plus there
              are specific exercises you can do (and others you should avoid!) that may help you sidestep a common
              pregnancy complication called{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/what-is-diastasis-recti/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Diastasis Recti
              </StyledLink>
              . Diastasis Recti is separation of the abdominal muscles, and it is both preventable and reversible
              without surgery!
            </div>
            <br />
            <div>
              A pelvic floor physical therapist can also teach you breathing techniques that will help you regulate the
              pressure inside your abdomen—which is key to avoiding Diastasis Recti, says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/joanie-j/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Joanie Johnson
              </StyledLink>
              , co-founder of Fit Pregnancy Club in New York City. "The physiotherapist you work with should address
              your breathing via your diaphragm as a first step," she says. "From there, they will teach you to activate
              and release your pelvic floor muscles in combination with your breath and then to wrap and engage your
              transverse ab muscles."
            </div>
            <br />
            <div>
              You might also be surprised to learn that physical therapy may be used to address certain types of
              infertility problems. For example,{' '}
              <StyledLink
                href="http://alternative-therapies.com/openaccess/ATHM_23_3_Rice.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                research
              </StyledLink>{' '}
              has shown that it may help women with blocked fallopian tubes or endometriosis substantially up their odds
              of getting pregnant. Physical therapists sometimes use the "Wurn technique" to break up adhesions within
              or between reproductive organs that are interfering with conception, or they may perform different types
              of hands-on therapy to treat{' '}
              <StyledLink
                href="http://clearpassage.com/site-resources/Hydrosalpinx-2006.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                hydrosalpinx
              </StyledLink>
              , a condition in which one or both fallopian tubes are filled with fluid.
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Physical therapy during pregnancy',
        asH2: true,
        text: (): JSX.Element => (
          <div>
            <div>
              During pregnancy your{' '}
              <StyledLink
                href="https://www.choosept.com/resources/detail/pregnancy-pains-causes-solutions-comprehensive-rel"
                target="_blank"
                rel="noopener noreferrer"
              >
                posture, joint alignment, muscle strength, and flexibility
              </StyledLink>{' '}
              may all shift in unexpected and sometimes uncomfortable ways that may affect your quality of life. A
              physical therapist can teach you a wide range of exercises, from Kegels that strengthen your pelvic floor
              to stretches designed to alleviate low back pain.{' '}
              <StyledLink
                href="https://www.jospt.org/doi/full/10.2519/jospt.2014.0505"
                target="_blank"
                rel="noopener noreferrer"
              >
                Research
              </StyledLink>{' '}
              has shown that exercises, manual techniques, and other aspects of physical therapy can substantially
              decrease back and pelvic pain and improve your ability to move around comfortably.
            </div>
            <br />
            <div>
              If you're not in pain but are starting to experience urinary incontinence, you'd likely benefit from
              seeing a physical therapist who specializes and gives you a treatment plan in{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/the-key-to-pelvic-floor-rehab/"
                target="_blank"
                rel="noopener noreferrer"
              >
                pelvic floor therapy
              </StyledLink>
              . While this treatment is often not explored until after a woman gives birth (if it's sought out at all),
              getting help sooner can prevent your symptoms from worsening. It may also make your eventual labor and
              delivery that much easier.
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Physical therapy after pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              In some countries, such as{' '}
              <StyledLink
                href="https://obgyn.ucsf.edu/news/what-french-get-so-right-about-taking-care-new-moms"
                target="_blank"
                rel="noopener noreferrer"
              >
                France
              </StyledLink>
              , postpartum physical therapy services are standard for every new mom. In the U.S. it's less common, but
              that may be changing: The{' '}
              <StyledLink
                href="https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2018/05/optimizing-postpartum-care"
                target="_blank"
                rel="noopener noreferrer"
              >
                American College of Obstetricians and Gynecologists
              </StyledLink>{' '}
              (ACOG) now recommends that every woman who's given birth be assessed during the "fourth trimester" for
              urinary and fecal incontinence, "with referral to physical therapy or urogynecology as indicated."
            </div>
            <br />
            <div>
              While many women practice Kegels on their own, it's important to make sure that you're performing them
              correctly—which is where a specialist comes in. Physical therapists work on this area using a variety of
              strategies, including biofeedback, to ensure that you know how to do these muscle contractions in a way
              that's most beneficial for your body.
            </div>
            <br />
            <div>
              A physical therapist can also help get your abdominal muscles back in shape which may help with pain
              management. Pregnancy{' '}
              <StyledLink
                href="https://www.choosept.com/Resources/Detail/5-tips-return-physical-activity-after-childbirth"
                target="_blank"
                rel="noopener noreferrer"
              >
                stretches and weakens
              </StyledLink>{' '}
              these muscles, which can lead to chronic pain and make everyday tasks like carrying groceries or even
              toting your baby around very uncomfortable.
            </div>
            <br />
            <div>
              Physical therapy is certainly crucial for women who develop Diastasis Recti. The sooner you start therapy
              for this problem the better your chances of it not becoming permanent. Stretching, bracing or taping the
              low back, and education about the best and worst exercises to perform are often part of the treatment, as
              is{' '}
              <StyledLink
                href="https://www.choosept.com/symptomsconditionsdetail/physical-therapy-guide-to-diastasis-rectus-abdominis"
                target="_blank"
                rel="noopener noreferrer"
              >
                postural training
              </StyledLink>{' '}
              that teaches you how to use proper posture during everyday activities so you don't further injure this
              area.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'MassageTherapy',
    title: 'Massage Therapy During Pregnancy | Robyn',
    slug: '/service/massage-therapy',
    description:
      "Many women would love to get a massage during pregnancy but aren't sure if it's safe to do so. Rest assured it is.",
    text: 'Hands-on bodywork helps relieve stress, ease pregnancy-related aches, and more.',
    h1: 'Massage Therapy',

    subtitles: [
      {
        id: '1',
        title: 'Massage before pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              There's little doubt that getting kneaded by a professional has major stress-busting benefits. Massage
              therapy may also help loosen muscle tension, relieve low back pain, and combat insomnia. These perks are
              helpful for your general health care and well-being at just about any stage of life, including when you're
              trying to get pregnant.
            </div>
            <br />
            <div>
              If you're struggling to conceive, you might have heard about "fertility massage" or the{' '}
              <StyledLink
                href="https://www.massagemag.com/mayan-fertility-massage-technique-87497/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Arvigo Techniques of Maya Abdominal Therapy
              </StyledLink>
              . While this method hasn't been scientifically proven to help you get pregnant,{' '}
              <StyledLink
                href="https://www.mercurynews.com/2017/01/06/maya-abdominal-massage-more-than-just-a-belly-rub-for-women/"
                target="_blank"
                rel="noopener noreferrer"
              >
                some women
              </StyledLink>{' '}
              who've undergone this type of massage therapy—which focuses on specific areas of the abdomen and aims to
              improve blood flow in the reproductive organs—have reported that it helped them get pregnant.
            </div>
            <br />
            <div>
              Other types of massage may be helpful, too: A{' '}
              <StyledLink href="https://pubmed.ncbi.nlm.nih.gov/25830275/" target="_blank" rel="noopener noreferrer">
                study
              </StyledLink>{' '}
              of women in Austria suggests that those who had 30-minute andullation therapy (relaxing massage using a
              vibrating device) had better odds of embryo implantation during IVF. Meanwhile, modalities like Swedish
              massage, deep tissue massage, and reflexology may help to relieve pain, ease stress, and address medical
              conditions in some women.
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Massage during pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Many women would love to get a massage during pregnancy but aren't sure if it's safe to do so. Rest
              assured that it is, provided you work with a licensed practitioner who is certified in prenatal massage.
            </div>
            <br />
            <div>
              Although it should be OK to get a massage at any time during your pregnancy, some therapists will not see
              pregnant clients until they have reached the second trimester. The risk of miscarriage is highest in the
              first trimester, and there are certain pressure points that may have the{' '}
              <StyledLink
                href="https://utswmed.org/medblog/massage-pregnancy-guidelines/"
                target="_blank"
                rel="noopener noreferrer"
              >
                potential to induce contractions
              </StyledLink>{' '}
              . Massage and bodywork might also be contraindicated later in your pregnancy if you have uncontrolled high
              blood pressure, pre-eclampsia, or another issue that puts you at high risk. When in doubt, it's always
              wise to check with your ob-gyn.
            </div>
            <br />
            <div>
              For most women, fortunately, prenatal massage is both safe and highly beneficial. Not only does{' '}
              <StyledH2>massage therapy during pregnancy</StyledH2> feel good, but{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2870995/"
                target="_blank"
                rel="noopener noreferrer"
              >
                research
              </StyledLink>{' '}
              has shown benefits of massage and positive side effects including lower levels of depression, anxiety, and
              leg and back pain. Pregnant women who get regular prenatal massages are also less likely to deliver
              prematurely, possibly thanks to the impact this therapy has on lowering levels of the stress hormone
              cortisol. It might lower the chances of having a{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5467308/"
                target="_blank"
                rel="noopener noreferrer"
              >
                low birth weight
              </StyledLink>{' '}
              baby as well.
            </div>
            <br />
            <div>
              During this type of massage session, your therapist should have you{' '}
              <StyledLink
                href="https://www.amtamassage.org/publications/massage-therapy-journal/massage-and-pregnancy-a-powerful-combination/"
                target="_blank"
                rel="noopener noreferrer"
              >
                lay on your side
              </StyledLink>{' '}
              rather than your back to ensure that you remain comfortable and that your baby continues getting proper
              circulation. Some therapists will use special pillows to position you or use a table that has a cutout for
              your belly so you can rest face-down.
            </div>
            <br />
            <div>
              If you enjoy{' '}
              <StyledLink
                href="https://www.mayoclinichealthsystem.org/hometown-health/speaking-of-health/essential-oils-and-pregnancy#:~:text=I%20recommend%20pregnant%20woman%20start,trigger%20symptoms%2C%20such%20as%20nausea."
                target="_blank"
                rel="noopener noreferrer"
              >
                aromatherapy
              </StyledLink>
              , keep in mind that smells you loved before you got pregnant might suddenly seem nauseating or repulsive.
              Consider skipping aromatherapy during your massage, or asking your therapist to put a small amount of
              essential oil on a cotton ball or towel so you can smell it yet easily remove it if you start to dislike
              the scent.
            </div>
            <br />
            <div>
              As your due date approaches, you might consider asking your massage therapist or a licensed acupuncturist
              about using acupressure to stimulate points that have been associated with{' '}
              <StyledLink
                href="https://www.medicalnewstoday.com/articles/323402"
                target="_blank"
                rel="noopener noreferrer"
              >
                inducing labor
              </StyledLink>
              . There isn't any hard evidence that this works, but as long as your baby is full-term it's generally
              considered safe to try. (Run it by your doctor first to be sure.)
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Massage after pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Pregnancy and delivery can take a toll on your body, as can nursing and carrying a baby who wants to be
              held all the time. Postpartum massage may help ease your discomfort and bolster your body's natural
              healing processes
            </div>
            <br />
            <div>
              Shortly after you give birth, massage may be useful for promoting lymphatic drainage, explains Robyn
              provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/sally-hayes/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sally Hayes
              </StyledLink>
              , CMT. "Many clients I see who have received an IV during labor and delivery return home more swollen than
              during their pregnancy. Specific lymphatic massage techniques can help reduce swelling, pain relief and
              flush the excess fluid by stimulating the flow of lymph. This will also assist with the removal of any
              chemicals, such as epidurals, oxytocin or other painkillers, from the body,"{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/3-benefits-of-a-postpartum-massage/"
                target="_blank"
                rel="noopener noreferrer"
              >
                she says
              </StyledLink>
              . "Some massage techniques will naturally stimulate the lymphatic system, but finding a massage therapist
              who has specifically studied lymphatic drainage is most effective."
            </div>
            <br />
            <div>
              Postpartum massage might help with your{' '}
              <StyledLink
                href="https://www.massagetherapycanada.com/postpartum-massage-1296/"
                target="_blank"
                rel="noopener noreferrer"
              >
                emotional recovery
              </StyledLink>
              , too. Giving birth can be transformative but also overwhelming, and there are a lot of hormonal and
              lifestyle changes that happen rapidly and can contribute to{' '}
              <StyledLink
                href="https://www.medscape.com/viewarticle/471895_4"
                target="_blank"
                rel="noopener noreferrer"
              >
                postpartum depression
              </StyledLink>{' '}
              and anxiety. Getting regular massages may help you sleep better and improve your mood, both of which may
              lower your risk of developing a serious mental health problem. Massage may also be a helpful adjunct for
              new moms who are already taking medication or getting other treatment for depression.
            </div>
            <br />
            <div>
              As you ease into new parenthood and your baby grows, you may be surprised by how much your shoulders and
              upper back hurt from cradling and feeding your baby. Massage can relieve some of that discomfort. An
              experienced provider can also teach you some simple stretches to employ at home in between professional
              treatments.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'Exercise',
    title: 'Postpartum Exercise | Robyn',
    slug: '/service/exercise',
    description:
      "Some physical activity is good for everyone’s overall health, including those who aren't trying to have a baby.",
    text:
      'Moving your body helps you get in shape before you conceive and remain healthy throughout your pregnancy and beyond.',
    h1: 'Exercise',
    subtitles: [
      {
        id: '1',
        title: 'Exercise before pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Some physical activity is good for everyone’s overall health, including those who aren't trying to have a
              baby. But if you have your eye on becoming a parent, there are some good reasons to take a close look at
              your fitness level and consider making some changes now.
            </div>
            <br />
            <div>
              For starters, most Americans are too sedentary, which increases the risk of a wide range of health
              problems and chronic diseases including obesity and diabetes. These issues can make it harder to get
              pregnant and also increase the risk of complications during pregnancy. Exercise has other health benefits
              like improving mental health by cutting stress and strengthening your heart, which will be important
              during pregnancy when it will have to pump more blood.
            </div>
            <br />
            <div>
              For these reasons and many others, the{' '}
              <StyledLink
                href="https://www.acog.org/news/news-releases/2020/03/acog-releases-updated-guidance-on-exercise-in-pregnancy-and-postpartum"
                target="_blank"
                rel="noopener noreferrer"
              >
                American College of Obstetricians and Gynecologists
              </StyledLink>{' '}
              (ACOG) recommends that women do both strength-conditioning and aerobic exercise (aka cardio) before,
              during, and after pregnancy.
            </div>
            <br />
            <div>
              Some women also benefit from practicing{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/yoga-for-fertility/"
                target="_blank"
                rel="noopener noreferrer"
              >
                yoga for fertility
              </StyledLink>
              , which is designed to promote blood flow to the uterus and ovaries and decrease inflammation throughout
              your body, as well as ease stress. It helps you get in touch with your body, too. "Many of the women I've
              spoken to have shared that their fertility challenges have made them feel disconnected from their bodies.
              That disconnect can make it pretty difficult to hear the subtle cues your body is giving you about what it
              needs to thrive," says Robyn{' '}
              <StyledLink
                href="https://wearerobyn.co/city/na/maternal-specialist/kendra-t/"
                target="_blank"
                rel="noopener noreferrer"
              >
                provider Kendra Tolbert
              </StyledLink>
              , MS, RDN, RYT, a dietitian and yoga teacher who specializes in fertility-related exercise and nutrition.
            </div>
            <br />
            <div>
              Specific yoga poses, like{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/how-does-yoga-really-complement-fertility/"
                target="_blank"
                rel="noopener noreferrer"
              >
                "legs up the wall" and "equal breath"
              </StyledLink>{' '}
              can be especially good for women undergoing treatments like IVF. Consider finding a yoga for fertility
              class or seeking out a{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialists/exercise/"
                target="_blank"
                rel="noopener noreferrer"
              >
                private consult with one of our experts
              </StyledLink>
              .
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Exercise during pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Decades ago, women were often advised to take it easy during pregnancy. Today, very few types of exercise
              are off the table. Although it's always smart to check in with your ob/gyn about the "rules" based on your
              personal health and risk factors, most pregnant women will benefit from{' '}
              <StyledLink
                href="https://www.acog.org/patient-resources/faqs/pregnancy/exercise-during-pregnancy"
                target="_blank"
                rel="noopener noreferrer"
              >
                starting or maintaining
              </StyledLink>{' '}
              a regular exercise routine.
            </div>
            <br />
            <div>
              Unless you are experiencing specific pregnancy complications,{' '}
              <StyledLink
                href="https://www.acog.org/patient-resources/faqs/pregnancy/exercise-during-pregnancy"
                target="_blank"
                rel="noopener noreferrer"
              >
                being active will not increase the chances of having a miscarriage
              </StyledLink>{' '}
              or giving birth prematurely. In fact, the CDC currently recommends that pregnant women get{' '}
              <StyledLink
                href="https://www.cdc.gov/physicalactivity/basics/pregnancy/index.htm#:~:text=Pregnant%20or%20postpartum%20women%20should,this%20activity%20throughout%20the%20week."
                target="_blank"
                rel="noopener noreferrer"
              >
                at least 150 minutes
              </StyledLink>{' '}
              of moderate physical activity spread out over the course of each week.
            </div>
            <br />
            <div>
              Research shows that exercise helps keep both mom and baby healthy. A 2016 Spanish{' '}
              <StyledLink
                href="https://www.sciencedaily.com/releases/2017/09/170904093621.htm"
                target="_blank"
                rel="noopener noreferrer"
              >
                study
              </StyledLink>
              , for instance, found that women who exercise while pregnant have a lower risk of pre-eclampsia
              (characterized by high blood pressure) and gestational diabetes. They're also less likely to put on too
              much weight, develop back pain or pelvic pain, or experience urinary incontinence.
            </div>
            <br />
            <div>
              What kind of exercise to do? In recent years, the general rule has been to{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/pregnancy-and-fertility-workout/"
                target="_blank"
                rel="noopener noreferrer"
              >
                continue doing whatever you were doing
              </StyledLink>{' '}
              before pregnancy, but there are some caveats. If you weren't doing enough physical activity before
              conceiving, you may need to step up your game a little. While this isn't the time to go from couch potato
              to marathon runner, almost everyone will benefit from gentle exercise like brisk walking or prenatal yoga.
            </div>
            <br />
            <div>
              Even if you were very active before, it might be time to make some changes because of how your body is
              changing. A good rule of thumb is to focus on what feels good to you and what will improve your general
              well-being. "Maybe you have lower back pain or chest pain after, or maybe your ankles and calves scream at
              you for a week after that 30-minute walk (in which case, maybe you swim or practice prenatal yoga), or
              maybe you feel amazing after a walk," says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/amy-w/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Amy Wolfe
              </StyledLink>
              , JD, RPYT, RYT500, a New York City-based yoga teacher. "Whatever the case may be, only ever do the
              exercise routines that{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/working-out-during-pregnancy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                feel good to your body
              </StyledLink>
              ."
            </div>
            <br />
            <div>
              If you enjoy certain activities but aren't sure how to do them safely or comfortably now, it's time to
              seek out a prenatal exercise specialist. "We want you to continue your favorite workouts as well, but
              pregnancy is the time to do them with more intension, better posture, and a deeper understanding of how to
              incorporate your entire core from diaphragm to the pelvic floor," says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/joanie-j/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Joanie Johnson
              </StyledLink>
              , co-founder of Fit Pregnancy Club in New York City.
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Exercise after pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              After giving birth, some women think about exercise only in terms of how it can help with weight loss and
              getting them back to their pre-pregnancy body weight. But physical activity can do so much more than that,
              from strengthening abdominal and back muscles that were strained during pregnancy to helping relieve some
              of the stress and fatigue that comes with being a new parent.
            </div>
            <br />
            <div>
              Most women who give birth vaginally can start moderate exercise such as walking within a few days of
              giving birth, but you may want to wait until your 6-week postpartum checkup when you can discuss resuming
              full workouts. If you had a C-section, make sure to ask your doctor about when you can safely resume
              activity. Since your body will still be in recovery mode for a while, it's best to ease back in slowly,
              perhaps by walking for{' '}
              <StyledLink
                href="https://www.acog.org/patient-resources/faqs/labor-delivery-and-postpartum-care/exercise-after-pregnancy#:~:text=Aim%20to%20stay%20active%20for,of%20exercise%20benefits%20your%20body."
                target="_blank"
                rel="noopener noreferrer"
              >
                20-30 minutes
              </StyledLink>{' '}
              a day.
            </div>
            <br />
            <div>
              While most types of moderate exercise are usually okay for postpartum women, some specific moves may be
              especially beneficial at this time.{' '}
              <StyledLink
                href="https://www.mayoclinic.org/healthy-lifestyle/labor-and-delivery/in-depth/exercise-after-pregnancy/art-20044596"
                target="_blank"
                rel="noopener noreferrer"
              >
                Kegels
              </StyledLink>
              , for instance, help to tone the pelvic floor muscles, and pelvic tilts can help with core strength.
            </div>
            <br />
            <div>
              Some pregnant women develop{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/what-is-diastasis-recti/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Diastasis Recti
              </StyledLink>
              —a separation of the muscles in the middle of your abdomen. If you're among them, seek out a pelvic floor
              physiotherapist, an expert who can prescribe a specific <StyledH2>postpartum exercise</StyledH2> program
              designed to address this problem.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'FertilityCoaching',
    title: 'Fertility Coach | Robyn',
    slug: '/service/fertility-coaching',
    description:
      'Trying to get pregnant can be confusing and overwhelming. Which medical treatments and alternative therapies are right for you?',
    text: 'The right coach can provide invaluable guidance and support',
    h1: 'Fertility Coaching',
    subtitles: [
      {
        id: '1',
        title: 'Fertility Coaching',
        text: (): JSX.Element => (
          <div>
            <div>
              Trying to get pregnant can be confusing and overwhelming. Which medical treatments and alternative
              therapies are right for you? How can you best deal with insensitive friends and family members who don't
              understand what you're going through? How can you manage the stress, depression, and anxiety you may feel?
              A trained fertility coach can help with any or all of the above.
            </div>
            <br />
            <div>
              A fertility coach isn’t a health care provider, so they won't order medical tests or perform procedures
              like IUI or IVF. Instead, this trained expert—who might also be called a fertility counselor or fertility
              therapist—will attempt to guide you, educate you, and support you while prioritizing your emotional
              wellbeing as you strive to get pregnant.
            </div>
            <br />
            <div>
              There are different types of fertility coaches, so it's wise to have some understanding of the support
              you're seeking when interviewing possible candidates. Some fertility coaches focus on alternatives and
              complementary techniques that are designed to increase your likelihood of conceiving either naturally or
              with the help of assisted reproductive technology.
            </div>
            <br />
            <div>
              Some fertility coaches perform{' '}
              <StyledLink
                href="https://todayspractitioner.com/womens-health/part-1-qa-with-catherine-gregory-author-of-fertile-the-wise-womans-guide-to-pregnancy-success/#.XvjGLyhKiUl"
                target="_blank"
                rel="noopener noreferrer"
              >
                massage
              </StyledLink>{' '}
              designed to improve uterine blood flow as part of their services; others train clients in specific
              breathing techniques, yoga sequences, or mindfulness strategies designed to help combat stress: At least{' '}
              <StyledLink href="https://pubmed.ncbi.nlm.nih.gov/26742022/" target="_blank" rel="noopener noreferrer">
                one study
              </StyledLink>{' '}
              has shown that practicing mindfulness meditation may increase the likelihood of getting pregnant via IVF.
            </div>
            <br />
            <div>
              An{' '}
              <StyledLink
                href="https://www.integrativefertilityinstitute.com/become-a-fertility-coach"
                target="_blank"
                rel="noopener noreferrer"
              >
                integrative fertility coach
              </StyledLink>{' '}
              will likely also teach you about how to implement certain diet, exercise, and other lifestyle changes
              designed to increase the odds of getting pregnant. Lifestyle changes may be especially beneficial to women
              with PCOS.
            </div>
            <br />
            <div>
              Whether you're working with a certified coach or a licensed psychologist, you should expect that some type
              of "talk therapy" will be part of the process. Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/lindsay-l/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Lindsay Liben
              </StyledLink>
              , LCSW, who specializes in infertility psychotherapy, provides emotional support by focusing her work on
              helping clients "process the wide range of emotions you are experiencing, which helps you to understand,
              grieve, and cope with your feelings."
            </div>
            <br />
            <div>
              <StyledLink
                href="https://wearerobyn.co/learn/infertility-therapy-explained/"
                target="_blank"
                rel="noopener noreferrer"
              >
                She notes
              </StyledLink>{' '}
              that it's common for people who are having trouble conceiving or going through fertility challenges to
              experience a roller coaster of emotions such as sadness, rage, loss, and/or depression. "[The struggle to
              get pregnant] also interferes with your interpersonal relationships, especially with your partner," she
              says. "You may feel like isolating or withdrawing instead of attending parties where you'll be judged. Or,
              maybe even asked: 'When are you going to give us a grandson?' There are so many 'new normals' that even
              the adjustment to all this change can feel like a traumatic experience."
            </div>
            <br />
            <div>
              Even if you don't develop serious depression or anxiety (though many people do), you may get overwhelmed
              by stress or caught off-guard by feelings you don't expect. Many people who are struggling to conceive or
              are undergoing fertility treatment start to feel like babies are everywhere and come easily to other
              people. Dragging yourself to another baby shower or forcing yourself to coo over your friend's newborn can
              feel tortuous to someone struggling with infertility, which is why Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/tasha-b/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Tasha Blasi
              </StyledLink>
              , an integrative fertility coach, employs a series of specific techniques designed to help clients deal
              with "pregnancy envy" and "baby envy."
            </div>
            <br />
            <div>
              "Like any of us that have had a prolonged fertility journey, 'faking it' becomes part of the day-to-day,"{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/how-to-deal-with-pregnancy-envy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                says Blasi
              </StyledLink>
              . "We do the obligatory 'awwwwws' at baby showers and wait until we are in the car to start crying. We
              know how to fake a nice expression when one of our friends tells us that she accidentally got pregnant
              (again). And we bite our tongues and hold our middle fingers down when someone offers us the brilliant
              advice to 'just relax' to finally achieve pregnancy. But faking it, and holding it in, doesn’t feel good.
              It can be quite suffocating and lead to anxiety during a time when your physical and mental health should
              be your top priority."
            </div>
            <br />
            <div>
              Many fertility coaches also specialize in helping clients process pregnancy loss such as miscarriage or
              stillbirth. "It is necessary to grieve a miscarriage because it is a healthy response to death. Mourning
              is how our conscious mind works through the pain," says Liben.
            </div>
            <br />
            <div>
              Some people seek out fertility coaches on their own, while others attend with a partner. Going with your
              partner may be especially beneficial if the process of trying to get pregnant has led to{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3288135/"
                target="_blank"
                rel="noopener noreferrer"
              >
                marital or sexual problems
              </StyledLink>
              . Sometimes{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/what-happens-when-you-send-husband-to-fertility-therapy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                men
              </StyledLink>{' '}
              even go to fertility counseling or coaching sessions by themselves to help process their own feelings and
              emotional strife.
            </div>
            <br />
            <div>
              Fertility coaches and counselors can also help you make difficult decisions, such as whether you should
              use an{' '}
              <StyledLink
                href="https://www.verywellfamily.com/reasons-to-see-an-infertility-counselor-1959981"
                target="_blank"
                rel="noopener noreferrer"
              >
                egg donor
              </StyledLink>{' '}
              or gestational carrier (surrogate), explore adoption, or decide to cease your current efforts to have a
              baby.
            </div>
            <br />
            <div>
              Since there are so many different reasons to hire a fertility coach—and many types of approaches that a
              coach might employ—asking a lot of questions during your initial consultation is key to finding the right
              fit.{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialists/fertility-coaches/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Start your search for a fertility coach here today.
              </StyledLink>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'Coaching',
    title: 'Pregnancy Coaching | Robyn',
    slug: '/service/coaching',
    description:
      'No matter how many books you read or classes you take, nothing can prepare you for the drastic changes that go along with bringing home a new baby.',
    text:
      'The right coach can help you acclimate more smoothly to fertility, pregnancy, postpartum and new parenthood.',
    h1: 'Coaching',
    subtitles: [
      {
        id: '1',
        title: 'Coaching',
        text: (): JSX.Element => (
          <div>
            <div>
              No matter how many books you've read or classes you've taken, nothing quite prepares you for the drastic
              changes that go along with bringing home a new baby. New parents often struggle with figuring out how to
              best care for their newborn, how to take care of their own bodies, and how to manage everyday logistics
              and emotions that can be overwhelming. The good news is you don't have to go it alone.
            </div>
            <br />
            <div>
              The right coach can help with almost anything, from body acceptance and pelvic floor problems to infant
              sleep and baby wearing. They can even help you transition back to work and make decisions about childcare.
              Whatever type of support you need as an aspiring, expecting, or new parent, there's a coach who can help.
            </div>
            <br />
            <div>
              There are many different types of coaches and a wide range of certifications they may have. Some
              specialize in general health coaching, whereas others focus on postpartum health. Working with a
              postpartum health coach may be extremely useful for new mothers who have developed Diastastis Recti or
              incontinence, or are recovering from a cesarean section. Even new moms who had a vaginal birth free of
              complications may find that their bodies take longer to heal than expected, or that they are more
              exhausted, stressed, and achy than they could have imagined.
            </div>
            <br />
            <div>
              Many coaches who are trained in postpartum health can help you develop an appropriate fitness plan aimed
              at helping you reclaim your strength and prioritize self-care. Others specialize in belly banding. Some
              coaches, such as integrative health coaches and wellness coaches, provide dietary advice designed to help
              you feel your best and get good nutrition despite your time constraints.
            </div>
            <br />
            <div>
              That kind of guidance is useful for so many new parents; if you have an underlying condition, it may be
              even more worthwhile. Some coaches like Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/melanie-snyder/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Melanie Synder
              </StyledLink>{' '}
              specialize in helping those who are struggling with autoimmune disease and/or chronic inflammation get
              their health back on track by making better lifestyle choices—either while they're trying to conceive or
              during the postpartum period.
            </div>
            <br />
            <div>
              Coaches help new parents with the emotional aspects of having a baby, too. Fertility coaches specialize in
              working with women preconception who are having trouble getting pregnant; there are also coaches who focus
              on helping parents grieve miscarriages and stillbirths. Even women who get pregnant easily and have a
              healthy baby might find themselves struggling with fear, anxiety, and depression, and the emotional
              support of a <StyledH2>pregnancy coach</StyledH2> can be instrumental in helping them thrive.
            </div>
            <br />
            <div>
              The isolation, hormonal fluctuations, and sleep deprivation lead many new moms to experience mood changes
              that can range from the "baby blues" to full-on postpartum depression. Although coaches are not licensed
              mental healthcare providers, they can{' '}
              <StyledLink
                href="https://www.girlsgonestrong.com/blog/articles/what-coaches-need-to-know-about-postpartum-depression/"
                target="_blank"
                rel="noopener noreferrer"
              >
                educate you about postpartum health
              </StyledLink>
              , help you feel less alone, and direct you to a psychologist or other mental health provider if necessary.
            </div>
            <br />
            <div>
              "We find that in postpartum, a lot of women are all feeling alone at the same time," says Robyn provider
              Chelsea Skaggs, founder of{' '}
              <StyledLink href="https://www.postpartumtogether.com/about-1" target="_blank" rel="noopener noreferrer">
                Postpartum Together.
              </StyledLink>{' '}
              "New moms deserve ongoing support, community, and empowerment. Postpartum doesn't have to be
              Pinterest-perfect. It's beautiful and it's messy all at the same time."
            </div>
            <br />
            <div>
              Working parents can also turn to coaching sessions to help them figure out how to best{' '}
              <StyledLink
                href="https://www.mother.ly/work/what-is-a-life-coach-and-why-working-moms-need-one"
                target="_blank"
                rel="noopener noreferrer"
              >
                juggle parenthood with their career
              </StyledLink>
              . Whether you're confused on how to land a promotion with a baby at home or anxious about putting your
              child in daycare, a coach can answer your questions and share what they've learned from working with other
              new parents who've dealt with similar issues. Coaches are so beneficial that{' '}
              <StyledLink
                href="https://www.nytimes.com/2016/07/23/your-money/why-companies-have-started-to-coach-new-parents.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                some big companies have started offering them as a perk
              </StyledLink>{' '}
              to employees who are new or expectant parents. The goal: keep employees happy so they'll stick with the
              company as their personal family life changes through the years.
            </div>
            <br />
            <div>
              If you're confused about all the new gear and gadgets that are supposed to make life with baby easier, a
              prenatal or postpartum coach is a great resource here as well. A coach can help you choose the right
              baby-wearing gear and teach you to wear it correctly; select the swings and bouncers that are most apt to
              soothe a colicky baby; instruct you in proper swaddling techniques; and much more.
            </div>
            <br />
            <div>
              Many coaches are also trained in infant sleep, which can have a major impact on the health of your entire
              family. Whether you're interested in "cry it out" or a more gentle approach, an experienced sleep coach
              can guide you toward the best method for your family so that your baby gets better quality sleep and
              sleeps for longer stretches during the night.
            </div>
            <br />
            <div>
              There are so many types of coaches that it's smart to think about what kind of support you need most
              before beginning your search.{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialists/coaching/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Check out our wide range of coaches here.
              </StyledLink>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'InfantSleep',
    title: 'Infant Sleep | Robyn',
    slug: '/service/infant-sleep',
    description: 'Help baby sleep better so everyone in your family can get the quality rest they need.',
    text: 'Help baby sleep better so everyone in your family can get the quality rest they need.',
    h1: 'Infant Sleep',
    subtitles: [
      {
        id: '1',
        title: 'Infant Sleep',
        text: (): JSX.Element => (
          <div>
            <div>
              Most new parents expect that the first few months with a newborn are going to be rough, especially at
              night. Newborns sleep a lot, but they do so in spurts, waking up to feed every few hours{' '}
              <StyledLink
                href="https://www.stanfordchildrens.org/en/topic/default?id=newborn-sleep-patterns-90-P02632#:~:text=Generally%2C%20newborns%20sleep%20a%20total,this%20can%20vary%20a%20lot."
                target="_blank"
                rel="noopener noreferrer"
              >
                no matter what time it is
              </StyledLink>
              . Eventually babies’ sleep patterns change, and most babies start sleeping through the night. Whether that
              happens at 3 months, 6 months, a year, or much later has to do with a number of factors.
            </div>
            <br />
            <div>
              Babies who breastfeed tend to take longer to sleep through the night, as they wake more often to nurse.
              Gastrointestinal issues, like gas or reflux, may interfere with baby’s sleep, as can teething. And just as
              with adults, some babies simply have an easier time drifting off and snoozing for long stretches whereas
              others toss and turn and fuss for what might seem like hours. While you can only control some of these
              factors, there are several steps you can take to promote long, deep sleep.
            </div>
            <br />
            <div>
              First, safe sleep is extremely important: Babies younger than age one should always sleep on a firm
              mattress in a crib, play yard or bassinet without any loose bedding, comforters, blankets, pillows,
              bumpers, or toys. Put your baby down on their back, not stomach (but if they flip to their tummy
              themselves as they get older that's okay). The American Academy of Pediatrics also recommends that new
              parents practice{' '}
              <StyledLink
                href="https://www.healthychildren.org/English/ages-stages/baby/sleep/Pages/A-Parents-Guide-to-Safe-Sleep.aspx"
                target="_blank"
                rel="noopener noreferrer"
              >
                room-sharing—but not bed-sharing
              </StyledLink>{' '}
              — for the first 6 months of baby's life. These strategies have all been associated with a lower risk of
              SIDS (sudden infant death syndrome) and sleep-related infant deaths.
            </div>
            <br />
            <div>
              When it comes to comfort, remember that babies sleep best when it's cool (69-72° F) and dark. A white
              noise machine often helps, too, because it mimics the sounds of the womb and can help drown out disruptive
              noises from elsewhere in your home or outdoors, says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/lauren-wolf/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Lauren Wolf
              </StyledLink>
              , a certified infant and child sleep consultant. Swaddling may be soothing, but only for babies up to{' '}
              <StyledLink
                href="https://www.stanfordchildrens.org/en/topic/default?id=newborn-sleep-patterns-90-P02632#:~:text=Generally%2C%20newborns%20sleep%20a%20total,this%20can%20vary%20a%20lot."
                target="_blank"
                rel="noopener noreferrer"
              >
                two months
              </StyledLink>{' '}
              of age. (Babies that are old enough to turn over can get stuck face down if they're in a swaddle.)
            </div>
            <br />
            <div>
              Having a calming routine is also very important. Around 6 weeks of age you can start a basic nap and
              bedtime routine, such as feed, diaper change, song, then put in the crib,{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/infant-sleep-5-best-tips-you-need-to-know/"
                target="_blank"
                rel="noopener noreferrer"
              >
                says Wolf
              </StyledLink>
              . The goal is to have your baby associate this pattern with sleep. Aromatherapy might also help; some
              research has shown that{' '}
              <StyledLink
                href="https://www.sciencedirect.com/science/article/abs/pii/S1130862120300322"
                target="_blank"
                rel="noopener noreferrer"
              >
                massage with lavender
              </StyledLink>{' '}
              helps babies fall asleep and stay asleep longer. There are pros and cons of using a pacifier; ask your
              pediatrician, coach or sleep consultant what’s best for your child.
            </div>
            <br />
            <div>
              If your baby is four to six months old and still not sleeping through the night, you may wish to consider
              some type of sleep training. There are many different ways to improve a baby’s sleep habits and train a
              baby to sleep longer, from "cry it out" to more gentle techniques. No one method is right for every baby,
              or every family. There are many books that can provide you with an overview of popular techniques, or
              consider enlisting the help of a trained{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialists/infant-sleep/"
                target="_blank"
                rel="noopener noreferrer"
              >
                sleep consultant
              </StyledLink>{' '}
              who will work with you to develop a personalized approach.
            </div>
            <br />
            <div>
              A consultant will also work with you to help schedule naps and bedtime. The quality of daytime sleep—and
              timing—can have a major impact on nighttime sleep.
            </div>
            <br />
            <div>
              Whether it's for a nap or at night, try to put your baby down when they're showing signs of sleepiness,
              such as rubbing their eyes or yawning. "If you’re currently rocking, feeding, or cuddling your little one
              as part of their sleep routine, then you may experience multiple wake ups throughout the night. When
              babies wake in the middle of the night at the end of a sleep cycle (which all humans do), they need the
              same sleep environment recreated in order to fall back asleep," says Wolf. "As new parents, if we gently
              teach our babies to fall asleep without assistance, in their cribs, then they will ultimately learn how to
              put themselves back to sleep and sleep for longer stretches."
            </div>
            <br />
            <div>
              A sleep specialist can also help you with your baby’s sleep schedule by identifying their "sleep window,"
              the period in which they're sleepy enough to drift off without a fight, says Robyn provider{' '}
              <StyledLink
                href="https://www.wearerobyn.co/maternal-specialist/Jessica-Sawicki"
                target="_blank"
                rel="noopener noreferrer"
              >
                Jessica Sawicki
              </StyledLink>
              , a professional sleep consultant in South Florida. "When they enter their sleep window, they will start
              secreting melatonin, a calming hormone that helps them fall asleep,"{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/how-many-hours-should-your-infant-sleep/"
                target="_blank"
                rel="noopener noreferrer"
              >
                she explains
              </StyledLink>
              . "If you miss their sleep window (i.e. you did not put them down when they were ready to sleep), they
              will begin to secrete cortisol, a stress hormone, and become overtired. If they are overtired and have
              cortisol in their body, it will be harder for them to settle and get a good sleep. They will also have
              more sleep problems throughout the night and early morning."
            </div>
            <br />
            <div>
              Helping your baby sleep well is crucial so that the entire family gets their sleep needs met. Parents who
              are sleep deprived aren't just tired:{' '}
              <StyledLink
                href="https://womensmentalhealth.org/posts/postpartum-depression-and-poor-sleep-quality-occur-together/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Research
              </StyledLink>{' '}
              has found that new moms who get less sleep have a higher risk of postpartum depression. Partners suffer
              too, as{' '}
              <StyledLink
                href="https://www.sciencedirect.com/science/article/abs/pii/S0266613820301108"
                target="_blank"
                rel="noopener noreferrer"
              >
                sleep deprivation and depression
              </StyledLink>{' '}
              go hand-in-hand with becoming a new parent more often than many people realize. While some sleep changes
              are inevitable when you become a parent, you shouldn't struggle indefinitely—and you don't have to go it
              alone.{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialists/infant-sleep/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Start your search for a specialist here
              </StyledLink>{' '}
              today.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'BirthEducation',
    title: 'Birth Education | Robyn',
    slug: '/service/birth-education',
    description:
      'Preparing to give birth can be exciting, overwhelming, or downright scary. Demystifying the process can go a long way toward easing your fears.',
    text: 'Learning about what to expect and how to cope can make for a better birth experience.',
    h1: 'Birth Education',
    subtitles: [
      {
        id: '1',
        title: 'Birth Education',
        text: (): JSX.Element => (
          <div>
            <div>
              Preparing to give birth can be exciting, overwhelming, or downright scary. If you're about to become a
              first time parent, you're probably unsure about what to expect, and demystifying the process can go a long
              way toward easing your fears. Even if you have older kids at home, there's a good chance you could use a
              refresher—or maybe you're hoping to learn some new strategies that will help you be more satisfied with
              the experience.
            </div>
            <br />
            <div>
              Every birth is different, and there's no guarantee for expectant parents that things will go exactly as
              planned. Childbirth education classes aren’t designed to have you follow a set script, but rather give you
              the information and tools you need so you can make the best choices for you and your growing family.
            </div>
            <br />
            <div>
              Research has confirmed the benefits: In one study, published in the{' '}
              <StyledLink href="https://pubmed.ncbi.nlm.nih.gov/24325752/" target="_blank" rel="noopener noreferrer">
                Journal of Midwifery and Women's Health,
              </StyledLink>{' '}
              women who participated in a mindfulness-based childbirth class reported feeling more confident and in
              control during labor and delivery. Another study, from the{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3489119/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Journal of Perinatal Education,
              </StyledLink>{' '}
              found that women who attended a prenatal education class were more likely to give birth vaginally (which
              carries a lower risk of complications compared to having a cesarean or C-section).
            </div>
            <br />
            <div>
              Whether you sign up for a group class or work with a private instructor, childbirth education should cover
              a wide range of topics including the stages of labor, pain relief options, and labor positions. You should
              learn about how to tell when you're in labor, when to go to the hospital/birth center (or call your
              midwife), and the changes you will experience as you get closer to delivery.
            </div>
            <br />
            <div>
              Pain management is often a big part of childbirth education, and there are options to fit a variety of
              personal preferences. Many women opt for medical pain relief, most often in the form of an epidural. A
              childbirth educator can explain how epidurals and other forms of anesthesia work and answer any questions
              you might have about their efficacy, how they might impact labor, and when you can request that they be
              administered.
            </div>
            <br />
            <div>
              Childbirth education also often covers relaxation and deep breathing designed to decrease pain and
              anxiety. Some educators focus on one specific technique, such as the{' '}
              <StyledLink
                href="https://www.mayoclinic.org/healthy-lifestyle/pregnancy-week-by-week/in-depth/pregnancy/art-20044568#:~:text=Childbirth%20classes%20offer%20your%20partner,visualization%20for%20coping%20with%20contractions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Bradley method
              </StyledLink>{' '}
              — which emphasizes partner support—or Lamaze, which focuses on breathing and massage. Hypnobirthing, which
              involves a form of self-hypnosis, is also popular.
            </div>
            <br />
            <div>
              "Hypnobirthing is a pain relief technique whereby pregnant women use a mixture of self-hypnosis,
              relaxation and breathing techniques to overcome or reframe pain in labor," says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/michal-t-margolese/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Michal Margolese
              </StyledLink>
              . "This gentle view of childbirth takes the stance that both parent and baby’s body were created and meant
              to do this, and a harmonious, almost holy experience is possible. You can start by enrolling in
              hypnobirthing classes or taking a hypnobirthing course. Pregnant women can then train their brain and
              elicit a deep relaxation response on demand thereby using the mind to master the body and using the
              natural hormones created for the benefit of the body rather than focusing on stress or expectations of
              discomfort during their birthing experience."
            </div>
            <br />
            <div>
              Margolese explains that hypnobirthing is often sought out by women who want to have a non-medicated birth,
              but that it can be a helpful add-on for anyone. "A natural birth is not necessarily the goal,"{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/hypnobirthing-whats-all-the-hype-about/"
                target="_blank"
                rel="noopener noreferrer"
              >
                she explains
              </StyledLink>
              . "Rather, my goal is to assist my client to be as relaxed as possible with whatever birth process she
              chooses and works in her individual circumstances."
            </div>
            <br />
            <div>
              Many childbirth educators can also target instruction to women who know they will be having a C-section,
              those trying for a VBAC (vaginal birth after cesarean), and those who are carrying twins or multiples.
            </div>
            <br />
            <div>
              If you're planning to give birth at a hospital or birth center, a class offered at that same facility
              might incorporate a tour of the labor and delivery ward. Consider that a chance to get a sneak peek at the
              site as well as ask questions about their policies, such as whether they allow partners to stay overnight,
              if your baby will be taken to a nursery or kept in your room, and whether a lactation consultant will be
              available during your stay.
            </div>
            <br />
            <div>
              Because new parents generally don't see a healthcare provider until six weeks postpartum, childbirth
              education usually includes some information on postpartum healing and self-care. It may also incorporate
              newborn care basics (like how to hold a baby and change a diaper) and possibly feeding/breastfeeding. (If
              you're planning to breastfeed, you may want to take a separate breastfeeding class prior to giving birth.)
            </div>
            <br />
            <div>
              A private birth educator can also spend some time helping you with your "birth plan," which is an outline
              of preferences that you may want to share with your ob/gyn, nurses, doula, and others who will be involved
              in the birth. A birth plan might include information about who you want in the room during labor and
              delivery, whether or not you plan to use medication, whether you plan to try to breastfeed your baby
              immediately after birth, and whether you want your baby bathed right away or would prefer to delay. You
              should also think about whether you will want to see your baby being born with the aid of a mirror and
              whether you want your partner to cut the umbilical cord.
            </div>
            <br />
            <div>
              The right{' '}
              <StyledLink
                href="https://wearerobyn.co/learn/hypnobirthing-whats-all-the-hype-about/"
                target="_blank"
                rel="noopener noreferrer"
              >
                birth educator
              </StyledLink>{' '}
              can guide you as you consider your options and look forward to the birth of your baby.
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'OccupationalTherapy',
    title: 'Occupational Therapy | Robyn',
    slug: '/service/occupational-therapy',
    description:
      'Living with a physical disability, such as a spinal cord injury, brain injury, cerebral palsy etc. Does not mean that becoming a parent is off-limits.',
    text: 'Get expert help so you can move through each day more comfortably and efficiently.',
    h1: 'Occupational Therapy',

    subtitles: [
      {
        id: '1',
        title: 'Occupational therapy before pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Carrying groceries. Preparing dinner. Doing laundry. Getting dressed. For some people, these kinds of
              daily activities get taken care of without much thought or trouble. But if you have a disability or live
              with chronic pain, these seemingly basic chores might be difficult and uncomfortable—and that's before you
              bring a baby into the equation.
            </div>
            <br />
            <div>
              Living with a physical disability, such as a spinal cord injury, brain injury, cerebral palsy or multiple
              sclerosis, doesn't mean that becoming a parent is off-limits. Many with these sorts of issues can and do
              have healthy pregnancies and babies. Getting excellent preconception care can tip the odds in your favor,
              especially if you incorporate occupational therapy.
            </div>
            <br />
            <div>
              Occupational therapists (OTs) work with people of all ages in different settings to ensure that they have
              the physical and emotional resources they need to conduct daily tasks as easily as possible. By seeking
              out{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC1592145/"
                target="_blank"
                rel="noopener noreferrer"
              >
                occupational therapy before you get pregnant
              </StyledLink>
              , you increase the chances of being able to take good care of yourself and your baby when you eventually
              become a parent.
            </div>
            <br />
            <div>
              Not only will occupational therapists work to address any current problems or limitations you may have,
              but they will think ahead and set plans in place so that you'll be able to cope well when you get pregnant
              and bring home your baby. Some OTs will also assess your{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC1592145/"
                target="_blank"
                rel="noopener noreferrer"
              >
                home environment
              </StyledLink>{' '}
              to determine whether you will require any special equipment to make pregnancy and newborn care easier and
              safer for you.
            </div>
            <br />
            <div>
              Even if you don't have a serious disability, opting for occupational therapy services before you conceive
              may be helpful if you struggle with{' '}
              <StyledLink
                href="https://www.verywellhealth.com/occupational-therapy-and-womens-health-2509982"
                target="_blank"
                rel="noopener noreferrer"
              >
                back pain, insomnia, anxiety, or incontinence
              </StyledLink>
              . You might also consider working with an OT who specializes in prenatal fitness if you simply want to get
              into top form before becoming pregnant.
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Occupational therapy during pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              When you're pregnant, your body changes—a lot. Not only does your midsection get rounder as your uterus
              expands, but breasts usually grow, ligaments relax, and your feet may become swollen. These shifts can
              make everyday activities much more challenging, and back pain and incontinence become relatively common.
            </div>
            <br />
            <div>
              An OT can help by teaching you exercises designed to improve your muscle strength, including strengthening
              your core, which helps to relieve back and pelvic pain. An OT can also work with you to incorporate
              lifestyle changes designed to combat pregnancy-related insomnia, anxiety, and depression.
            </div>
            <br />
            <div>
              If you had a physical disability before getting pregnant, seeing an OT now can be especially important.
              The above-mentioned physiological changes that occur during pregnancy tend to be more severe among women
              with disabilities, but occupational therapy programs can improve your chances of{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC1592145/"
                target="_blank"
                rel="noopener noreferrer"
              >
                avoiding serious complications
              </StyledLink>{' '}
              during pregnancy and childbirth.
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Occupational therapy after pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              Welcoming a new baby into your home is amazing—and stressful! It can also be physically challenging,
              especially if you live with an underlying physical disability or developed pain or mobility problems
              during pregnancy or childbirth. An OT can help in a number of different ways.
            </div>
            <br />
            <div>
              Many develop back pain during pregnancy, which doesn't necessarily resolve the minute you give birth.
              Postpartum shoulder and neck pain is also common and often exacerbated by carrying and feeding a baby. An
              OT can teach you exercises designed to strengthen your core, stretch tense muscles, and teach you to
              maintain proper alignment while tending to your baby and carrying out other everyday tasks.
            </div>{' '}
            <br />
            <div>
              Some OTs have additional training in breastfeeding. Others specialize in{' '}
              <StyledLink
                href="https://www.verywellhealth.com/occupational-therapy-and-womens-health-2509982"
                target="_blank"
                rel="noopener noreferrer"
              >
                pelvic floor therapy
              </StyledLink>
              , which can be key to addressing childbirth-related incontinence.
            </div>
            <br />
            <div>
              Although many people only think about occupational therapy in terms of physical movement and motor skills,
              OTs often help with mental stress as well. Many OTs educate clients about body image, self-acceptance, and
              mindfulness techniques, all of which can help ease postpartum depression and anxiety.{' '}
              <StyledLink
                href="https://onlinelibrary.wiley.com/doi/full/10.1111/1440-1630.12225"
                target="_blank"
                rel="noopener noreferrer"
              >
                Research
              </StyledLink>{' '}
              has shown that creating an early intervention plan can lead to significant improvements in a new parent's
              mental health and overall sense of well-being. OT can help alleviate postpartum anxiety and encourage{' '}
              <StyledLink
                href="https://www.tandfonline.com/doi/full/10.1080/0164212X.2011.566165?src=recsys"
                target="_blank"
                rel="noopener noreferrer"
              >
                better bonding
              </StyledLink>{' '}
              with your baby.
            </div>
            <br />
            <div>
              An OT may also help with feeding and sleep routines, which in turn helps to build your confidence as you
              get settled into new parenthood. While any new parent may benefit from self-care through occupational
              therapy, those who had a{' '}
              <StyledLink
                href="https://soar.usa.edu/cgi/viewcontent.cgi?article=1006&context=casmfall2019"
                target="_blank"
                rel="noopener noreferrer"
              >
                premature baby that was in the NICU
              </StyledLink>{' '}
              might especially benefit from this extra support.
            </div>
            <br />
            <div>
              If you live with a physical disability that limits your mobility, an OT's help can be invaluable. The
              right OT should make sure that you have the resources you need—including{' '}
              <StyledLink
                href="https://www.rcot.co.uk/news/role-occupational-therapists-maternity-care-pathway"
                target="_blank"
                rel="noopener noreferrer"
              >
                equipment and support
              </StyledLink>{' '}
              from others—so that you and your new baby have the best chance of thriving during this transition.{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialists/occupational-therapy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Start your search for a great occupational therapist here.
              </StyledLink>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'Chiropractic',
    title: 'Chiropractic Adjustment | Robyn',
    slug: '/service/chiropractic',
    description:
      'Adjusting your spine is just part of what a chiropractor can do. Chiropractic care can play an important role on your journey to parenthood.',
    text:
      'Adjusting your achy spine is just part of what a chiropractor can do. Chiropractic care can play an important role at every stage of your journey to parenthood.',
    h1: 'Chiropractic',
    subtitles: [
      {
        id: '1',
        title: 'Chiropractic care before pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              You probably know that chiropractors can use hands-on manipulation and other manual therapies to improve
              the alignment of your spine and relieve a variety of aches and pains. Even if that's never interested you
              much before, now that you're trying to get pregnant it may be well worth adding a chiropractor to your
              healthcare team.
            </div>
            <br />
            <div>
              Chiropractic therapy can address common complaints like back, shoulder, and neck pain, any of which may be
              caused by stress or worsened by it. While stress doesn't directly cause infertility, both women and men
              who are plagued by high levels of stress are more apt to experience fertility problems.
            </div>
            <br />
            <div>
              There's also reason to believe that certain chiropractic treatments might play a role in helping some
              women get pregnant. While chiropractors don't exactly perform "fertility treatments," they can employ a
              wide variety of techniques and treatment options that may, in turn, help you conceive. For instance,{' '}
              <StyledLink href="https://pubmed.ncbi.nlm.nih.gov/28664223/" target="_blank" rel="noopener noreferrer">
                research
              </StyledLink>{' '}
              has found that women with scoliosis are somewhat more likely to suffer from infertility compared to women
              who don't have an abnormally curved spine. A chiropractor may be able to help by teaching you{' '}
              <StyledLink
                href="https://www.cleveland.edu/blog-post/~post/what-do-chiropractors-dofor-scoliosis-20190613/#:~:text=Many%20adults%20with%20scoliosis%20find,and%20strengthening%20their%20body's%20core.&text=Procedures%20and%20ideas%20to%20stay%20active%20without%20increasing"
                target="_blank"
                rel="noopener noreferrer"
              >
                strengthening exercises
              </StyledLink>{' '}
              that bolster your core and take pressure off painful areas.
            </div>
            <br />
            <div>
              There have also been some{' '}
              <StyledLink
                href="https://www.newswise.com/articles/studies-explore-link-between-chiropractic-care-and-fertility#:~:text=The%2012%20studies%20in%20the,amenorrhea%2C%20colitis%2C%20or%20trauma./"
                target="_blank"
                rel="noopener noreferrer"
              >
                case reports
              </StyledLink>{' '}
              of women with misalignment of the vertebrae in their spine who were having trouble conceiving yet were
              able to get pregnant after undergoing a series of <StyledH2>chiropractic adjustments</StyledH2>. Because
              your skeleton and nervous system are closely intertwined, a chiropractor may be able to help correct
              nervous system issues that may be interfering with conception.
            </div>
            <br />
            <div>
              Even if you don't believe you'll have any trouble conceiving, chiropractic services may be able to assist
              you in{' '}
              <StyledLink
                href="https://www.gobeaconhealth.com/4-common-misconceptions-pre-conception-chiropractor-care/#:~:text=While%20chiropractic%20adjustments%20can%20help,body%20undergoes%20the%20stress%20of"
                target="_blank"
                rel="noopener noreferrer"
              >
                getting your body into top form
              </StyledLink>{' '}
              before you begin carrying a baby. By nudging your spine and pelvis into proper alignment now you should be
              less likely to suffer back and joint pain later. Some women even say that seeing a chiropractor made their
              labor and delivery less taxing.
            </div>
          </div>
        ),
      },
      {
        id: '2',
        title: 'Chiropractic care during pregnancy',
        text: (): JSX.Element => (
          <div>
            <div>
              During pregnancy your body changes in even more ways than you might imagine. As your belly expands, the
              extra pressure—combined with a relaxing of your ligaments—may lead to low back pain.{' '}
              <StyledLink
                href="https://familydoctor.org/changes-in-your-body-during-pregnancy-second-trimester/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Pelvic and hip pain
              </StyledLink>{' '}
              are also common, as are leg cramps and swelling.
            </div>
            <br />
            <div>
              About{' '}
              <StyledLink
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2647084/"
                target="_blank"
                rel="noopener noreferrer"
              >
                half
              </StyledLink>{' '}
              of all pregnant women develop back pain. If you're among them, you may be reluctant to rely too heavily on
              over-the-counter medications for pain management, which makes sense given the impact they may have on your
              developing baby. Chiropractic care is a natural, drug-free alternative that can be very effective. In one
              study,{' '}
              <StyledLink href="https://chirobalance.co.nz/pregnancy/" target="_blank" rel="noopener noreferrer">
                75% of pregnant women
              </StyledLink>{' '}
              who were in pain said that chiropractic treatments helped them feel better.
            </div>
            <br />
            <div>
              <StyledH2>Chiropractic adjustments</StyledH2> should be{' '}
              <StyledLink
                href="https://www.acatoday.org/News-Publications/Publications/ACA-Blogs/ArtMID/6925/ArticleID/394/5-Conditions-Chiropractic-Care-Can-Improve-During-Pregnancy"
                target="_blank"
                rel="noopener noreferrer"
              >
                targeted to relieve specific types of pain
              </StyledLink>{' '}
              related to the musculoskeletal system, including sciatica (which can develop if your baby is putting
              pressure on your sciatic nerve) and round ligament pain, which occurs when the ligaments surrounding the
              abdominal wall stretch to accommodate the growth of your uterus.
            </div>
            <br />
            <div>
              Toward the end of your pregnancy, your baby should be positioned with their head down in preparation for
              delivery. If your baby is breech (feet first), a C-section becomes likely—unless the baby turns before you
              go into labor. While a chiropractor can't directly turn a baby, they can use an effective treatment called
              the{' '}
              <StyledLink
                href="https://www.donohoechiropractic.com/the-webster-technique-a-chiropractic-technique-to-turn-breech-babies/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Webster technique
              </StyledLink>{' '}
              to encourage them to shift into the right position. This technique entails adjusting the alignment of the
              sacrum as well as massaging and stretching of the spots at which uterine ligaments attach to the wall of
              your abdomen. According to one{' '}
              <StyledLink href="https://pubmed.ncbi.nlm.nih.gov/12183701/" target="_blank" rel="noopener noreferrer">
                Canadian study
              </StyledLink>
              , the Webster technique is highly effective at getting a baby to turn.
            </div>
            <br />
            <div>
              A chiropractor can also help you keep your immune system strong, says Robyn provider{' '}
              <StyledLink
                href="https://wearerobyn.co/maternal-specialist/allison-adams/ "
                target="_blank"
                rel="noopener noreferrer"
              >
                Allison Adams,
              </StyledLink>{' '}
              DC, a California-based chiropractor. “A mother’s immune system during pregnancy is of the utmost
              importance,” she says. “Chiropractors are trained to help you not only with chiropractic adjustments, but
              can also guide you with proper nutrition recommendations, supplementation and lifestyle modifications
              making you less vulnerable to illness.”
            </div>
          </div>
        ),
      },
      {
        id: '3',
        title: 'Chiropractic care after childbirth',
        text: (): JSX.Element => (
          <div>
            <div>
              Everyone knows that giving birth can be painful, and most also understand why being pregnant might be
              uncomfortable. What's not discussed as often is the fact that many women continue to experience
              discomfort—or develop new aches and pains—after giving birth.
            </div>
            <br />
            <div>
              According to the{' '}
              <StyledLink
                href="https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2018/07/postpartum-pain-management"
                target="_blank"
                rel="noopener noreferrer"
              >
                American College of Obstetrics and Gynecology
              </StyledLink>{' '}
              (ACOG), "pain and fatigue are the most common problems reported by women in the early postpartum period."
              Pain can make it hard for new moms to take care of themselves and their new babies and increases the risk
              of postpartum depression. It also prompts some to turn to opioids for relief.
            </div>
            <br />
            <div>
              Back pain is one frequent issue, as new parents tend to be hunched over while breast or bottle feeding or
              rocking and cradling their newborns. And of course a new mom's body is also recovering from the strain of
              pregnancy and childbirth.
            </div>
            <br />
            <div>
              While pain-relieving medications are sometimes unavoidable, certain drugs aren't safe to use while
              breastfeeding. <StyledH2>Chiropractic adjustments</StyledH2>, however, can help you feel better and reduce
              pain without the risk of unwanted side effects.
            </div>
            <br />
            <div>
              Chiropractors in the Robyn network can provide a wide range of services for anyone at various stages of
              life.
            </div>
          </div>
        ),
      },
    ],
  },
];
